'use client';

// Раздел «Контакты» — живая база подписчиков ботов клиента (SCRIPTS-MODE-2 Ф6.3).
// Данные пишет узел «Контакты» из сценариев; здесь — просмотр, поиск, фильтр по меткам,
// карточка человека МОДАЛКОЙ (простая панель — sheet, канон §11), правка и удаление.
// Устройство раздела — как у «Продуктов» (владелец 2026-08-03): в колонке только фильтры
// (поиск и метки, метка — куском пути /app/contacts/tag/<метка>), люди — рядами справа.
// Вёрстка — канон UI-TEMPLATE-CANON (эталон «Модели»): .ai-bar/.ai-search, пузыри
// .ai-vgroup, кружки .svc-circle; чипы .tpl-chip остались только показом меток в карточке
// (отбор с 2026-08-04 живёт кружком-фильтром в шапке страницы: метки, канал, аккаунт).
// В списке — лицо человека (фото профиля мессенджера, общее с «Сообщениями»), без фото —
// плитка канала.
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { refreshTagCaches } from '@/lib/tag-caches';
import { usePaneVisible } from '@/lib/pane-visible';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { usePersistentData, usePersistentPeek } from '@/lib/use-persistent-data';
import { awaitPersistent } from '@/lib/cabinet-warm';
import { warmTarget } from '@/lib/warm';
import { useWarmInView } from '@/lib/warm-in-view';
import { CONTACTS_PAGE, CONTACTS_SEARCH_MIN, contactsCursor } from '@/lib/contacts-page';
import { Shape, Sk, useShapeRecord } from './skeleton';
import { ChannelIcon, channelHex, nativeChannelTile } from './bot-templates';
import { useSlidePanel } from './native-layer-host';
import { askConfirm, askPrompt } from './ask';
import { readPhoneLook } from './layout-mode';
import { entryFolder } from '@/lib/chats-entry';
import { ListEmptyNote, dialogTitle, type DialogListRow } from './messages';
import { isOwnPanelHref, panelAddress, useAddressPick } from './panel-address';
import { sectionUrl } from '@/lib/cabinet-urls';
import { PageAction, PaneActiveContext } from './page-action';
import { DEmpty, DKV, DWarn, VGroup } from './drawer-kit';
import { FFormError, FHint, FSwipeRow } from './form-kit';
import {
  KButton,
  KCheckbox,
  KInput,
  KListGroup,
  KListRow,
  KListSection,
  KSwitch,
} from '@scriptera/ui/kit';
import { FilterGlyph, FilterRow, LayerFilterHand } from './sheet-filter';
import { KBareButton } from '@scriptera/ui/kit';
import { CHAINS_HIDDEN } from '@/lib/hidden-features';
import { chainTagWords } from '@/lib/template-wizards/chain';
import {
  AudienceFilterSheet,
  useAudienceWords,
  useBotOptions,
  useLetterOptions,
  useTagOptions,
  type PickOption,
} from './audience-filter-sheet';
import { audienceCount, dayDate, type AudienceValue } from './audience-value';
import {
  chainCardGroups,
  chainCardPage,
  chainStopWords,
  CT_CHAIN_BACK,
  CT_CHAIN_BACKIN,
  CT_CHAIN_MSG,
  CT_CHAIN_SKIP,
  CT_CHAIN_STOP,
  type ChainForecastResp,
} from './contact-chain-card';
import { audApplySet, AUD_KEYS, type AudOption } from './audience-rows';
import { useAudDict } from './audience-dict';
import { useRangeLabel } from './date-range';
import { FoldersStrip } from './messages-folders';
import { searchPhaseClass, useSearchMorph } from './search-morph';
import { FolderPage, useFolderPager } from './folder-pager';
import { useFolderCount } from './folder-store';
import { askNativeConfirm, hasNativeConfirm } from './confirm-sheet';
import {
  accountRoleHex,
  closeNativeSettingsPage,
  openNativeSettingsPage,
  pushNativeSettingsPage,
} from '@/lib/account-native';
import { log as journalLog } from '@/lib/client-journal';
import { msgShortWhen } from '@/lib/msg-dates';
import {
  contactHistoryGroups,
  CT_MORE,
  type ContactCardRowsWords,
  type ContactHistoryBotRow,
  type ContactHistoryLetterRow,
} from '@/lib/contact-card-rows';
import { fullNameNote, shortName } from '@/lib/display-name';
import { openDialogNative } from '@/lib/open-dialog';
import {
  CONTACT_FOLDERS_URL,
  CT_FILTER_PAGE,
  CT_FOLDERS_PAGE,
  CT_ROW,
  askPassedRangeNative,
  CT_FOLDERS_OPEN_EVENT,
  contactFoldersApi,
  ctFilterRootPage,
  ctFilterSubPage,
  innerKey,
  rulesToValue,
  useContactFolders,
  useContactFolderWord,
  useContactFoldersWeb,
  valueToRules,
} from './contact-folders';
import { HeadFolders, HeadSearch, ListSearch } from './list-head';
import type { ContactFolderRules } from '@scriptera/db/contact-folders';
import { isServiceMark } from '@scriptera/db/bot-chains';
import { useNativePhotos } from './native-icon';
import { shortAccountName } from '@/lib/account-folder-name';
import { useIsDark } from './n8n-icon';
import { circleColor } from '@/lib/circle-color';
import { useRouter } from '@/lib/cabinet-nav';
import { isNativeApp, nativeBridge, nativeMethod, readRoleHex } from '@/lib/native-app';
import type {
  NativeAccountField,
  NativeAccountGroup,
  NativeAccountHero,
  NativeAccountPage,
  NativeChatInfoData,
} from '@/lib/native-app';

/** Ключ и адрес списка людей — ОДНИ на кабинет: по ним живут и веб-раздел, и родная
 *  страница «Контактов» (общий стор usePersistentData, один запрос на двоих). */
const CONTACTS_KEY = 'cabinet:contacts';
const CONTACTS_URL = '/api/cabinet/contacts';


/**
 * Что РЯДУ списка нужно знать про человека. Отдельно от полной карточки (ContactRow),
 * потому что тем же рядом рисуется список «кто получит рассылку» (ТЗ 11): там у человека
 * есть всё видимое, но нет переменных карточки — и это не повод рисовать второй ряд.
 */
export interface ContactLine {
  id: string;
  channel: string;
  name: string;
  username: string;
  /** Фото человека из профиля мессенджера — ссылка нашего бакета (та же, что в «Сообщениях»). */
  avatarUrl: string;
  phone: string;
  email: string;
  tags: string[];
  lastSeenAt: string;
  createdAt: string;
}

export interface ContactRow extends ContactLine {
  externalId: string;
  /** Аккаунт/бот, которым человек достижим (ключ тот же, что у аккаунтов «Сообщений»). */
  botId: string;
  vars: Record<string, string>;
  /** «Остановил бота» (ТЗ 15): дата, когда человек остановил бота клиента. Пусто — не
   *  останавливал. Ставит приёмник вебхуков и отказ отправки; рассылки таких не берут. */
  stoppedAt?: string;
  /** «НЕ ПИСАТЬ» (MESSAGE-COMPOSER В4): человек попросил его не беспокоить — ни цепочки,
   *  ни рассылки его не берут. Ставится переключателем этой карточки и кнопкой «Не
   *  пишите мне» под письмом. */
  doNotDisturb?: boolean;
  /** Адрес переписки с человеком (владелец 09.09.2026): карточка ведёт в неё кнопкой
   *  «Открыть переписку». Пусто — переписки нет и кнопки не бывает. */
  dialogId?: string;
}

/** Аккаунт клиента для фильтра списка (свод общий с «Сообщениями», listBotAccounts). */
interface ContactAccount {
  channel: string;
  key: string;
  name: string;
  /** Адрес нашей ручки с фото аккаунта (П91); нет — буква. */
  avatarUrl?: string;
  /** Подключение отключено, переписки остались (П90): строка со сноской, выбирается. */
  off?: boolean;
}

interface ContactsResp {
  ok: boolean;
  /** Всего людей в отборе. Считает сервер и ТОЛЬКО на первой странице (SCALE-PLAN Е2). */
  total?: number;
  /** Аккаунты клиента — тоже только с первой страницей (от страницы к странице не меняются). */
  accounts?: ContactAccount[];
  contacts: ContactRow[];
  /** Дальше есть что грузить — та же мерка, что у «Чатов» (страница пришла полной). */
  more?: boolean;
}

/** Ключ кэша по адресу списка — ОДНИМ правилом у веб-раздела, родной страницы и прогрева
 *  соседних папок: другой порядок частей дал бы второй кэш того же списка. */
const listKeyOf = (url: string): string =>
  url === CONTACTS_URL ? CONTACTS_KEY : `${CONTACTS_KEY}:${url}`;

const acceptList = (raw: unknown): boolean => !!(raw as ContactsResp)?.ok;

/**
 * СПИСОК ЛЮДЕЙ СТРАНИЦАМИ (docs/SCALE-PLAN.md Е2) — ОДНА рука на веб-раздел и на родную
 * страницу приложения. Приём тот же, что у «Чатов» (messages.tsx): кэш кабинета держит
 * ПЕРВУЮ страницу, догруженные живут местной памятью и выбрасываются, как только сменился
 * адрес отбора, — иначе к новому списку снизу прилипали бы люди старого.
 *
 * `auto` — сколько людей дотянуть ФОНОМ, не дожидаясь прокрутки. Родному списку
 * приложения без этого никак: контракт моста заморожен (запрет №8), события «список
 * подошёл к концу» у страниц «Аккаунта» нет, и просить следующую страницу ему нечем.
 */
function useContactsPages(
  url: string,
  on: boolean,
  auto = 0,
): {
  data: ContactsResp | null;
  contacts: ContactRow[];
  hasMore: boolean;
  busy: boolean;
  loadMore: () => void;
  loading: boolean;
  error: boolean;
  reload: () => void;
} {
  const { data, loading, error, reload } = usePersistentData<ContactsResp>(
    listKeyOf(url),
    on ? url : '',
    { accept: acceptList },
  );
  const [tail, setTail] = useState<{ url: string; rows: ContactRow[]; more: boolean | null }>({
    url,
    rows: [],
    more: null,
  });
  // Правка состояния ПРЯМО В РЕНДЕРЕ — так React и советует помнить прежнее значение.
  if (tail.url !== url) setTail({ url, rows: [], more: null });
  const [busy, setBusy] = useState(false);

  const contacts = useMemo(() => {
    // ОДИН ЧЕЛОВЕК — ОДНА СТРОКА: первую страницу кабинет перечитывает сам (показ панели,
    // возврат к вкладке), и человек, поднявшийся наверх новой репликой, оказался бы и в
    // свежей первой странице, и в догруженном хвосте. Побеждает страница, что выше.
    const seen = new Set<string>();
    const out: ContactRow[] = [];
    for (const c of [...(data?.contacts ?? []), ...(tail.url === url ? tail.rows : [])]) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  }, [data, tail, url]);
  const hasMore = (tail.url === url ? tail.more : null) ?? data?.more ?? false;

  const loadMore = useCallback(() => {
    const last = contacts[contacts.length - 1];
    if (!last || busy || !hasMore) return;
    setBusy(true);
    void (async () => {
      try {
        const sep = url.includes('?') ? '&' : '?';
        const r = await fetch(`${url}${sep}after=${encodeURIComponent(contactsCursor(last))}`, {
          cache: 'no-store',
        });
        const j = (await r.json()) as ContactsResp;
        // Пока страница ехала, отбор могли сменить — чужой хвост в новый список не кладём.
        if (j?.ok) {
          setTail((prev) =>
            prev.url === url ? { url, rows: [...prev.rows, ...j.contacts], more: !!j.more } : prev,
          );
        }
      } catch {
        /* обрыв сети — ловушка прокрутки попросит страницу ещё раз */
      } finally {
        setBusy(false);
      }
    })();
  }, [contacts, busy, hasMore, url]);

  // ФОНОВАЯ ДОТЯЖКА (для родного списка): страницы едут одна за другой, пока не наберётся
  // `auto` человек. Первый кадр от этого не ждёт — он рисуется первой же страницей.
  // Закрытую страницу не догружаем вовсе (`on`): кэш первой страницы у неё остаётся с
  // прошлого захода, и без этой оговорки хвост ехал бы, пока человек смотрит другой экран.
  useEffect(() => {
    if (!on || !auto || busy || !hasMore || contacts.length === 0 || contacts.length >= auto)
      return;
    const id = window.setTimeout(() => loadMore(), 150);
    return () => window.clearTimeout(id);
  }, [on, auto, busy, hasMore, contacts.length, loadMore]);

  return { data: data ?? null, contacts, hasMore, busy, loadMore, loading, error, reload };
}

/**
 * НАХОДКИ ПОИСКА — С СЕРВЕРА (SCALE-PLAN Е2, приём «Чатов»). В кабинете лежит страница
 * списка, поэтому свой отбор по ней искал бы в горстке людей; серверу слово уезжает через
 * четверть секунды после последней буквы. Выдача поиска не листается — это первая
 * страница находок, как и у «Чатов».
 *
 * Пока ответ едет (и пока слово короче мерки), на экране остаётся отбор своими силами —
 * по уже загруженному: человек видит результат сразу, а не через сеть.
 */
function useContactsSearch(
  url: string,
  needle: string,
): { q: string; contacts: ContactRow[] } | null {
  const [found, setFound] = useState<{ q: string; contacts: ContactRow[] } | null>(null);
  useEffect(() => {
    // Выдача стирается ТОЛЬКО на пустой строке (правило «Чатов»): стёртая одна буква не
    // должна оставлять человека с пустым экраном.
    if (!needle) {
      setFound(null);
      return;
    }
    if (needle.length < CONTACTS_SEARCH_MIN) return;
    let alive = true;
    const id = window.setTimeout(() => {
      void (async () => {
        try {
          const sep = url.includes('?') ? '&' : '?';
          const r = await fetch(`${url}${sep}q=${encodeURIComponent(needle)}`, {
            cache: 'no-store',
          });
          const j = (await r.json()) as ContactsResp;
          if (alive && j?.ok) setFound({ q: needle, contacts: j.contacts ?? [] });
        } catch {
          /* обрыв сети — прежние находки остаются на экране */
        }
      })();
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(id);
    };
  }, [url, needle]);
  return found;
}

// Каналы контактов — словарь узла «Контакты» (bot-contacts.ts). Брендовые имена не переводим.
const CHANNEL_LABELS: Record<string, { ru: string; en: string }> = {
  telegram: { ru: 'Telegram', en: 'Telegram' },
  instagram: { ru: 'Instagram', en: 'Instagram' },
  vk: { ru: 'ВКонтакте', en: 'VK' },
  max: { ru: 'MAX', en: 'MAX' },
  chat: { ru: 'Чат на сайте', en: 'Site chat' },
  form: { ru: 'Форма на сайте', en: 'Site form' },
};

function channelLabel(channel: string, locale: string): string {
  const l = CHANNEL_LABELS[channel];
  return l ? (locale.startsWith('en') ? l.en : l.ru) : channel;
}

/**
 * ПЕРВАЯ БУКВА ДЛЯ КРУГА-ЗАГЛУШКИ — БЕЗ «@» (владелец 09.09.2026, ТЗ «Контакты» п.3).
 * У человека без имени именем стоит его логин («@ivanov»), и в круге стояла собачка
 * вместо буквы. Собачку логина пропускаем — берём первую букву самого логина.
 * Натив считает так же (`ScrLetterTile.initial(for:)`, ScrStyle.swift): разойдутся —
 * буква в приложении и в браузере станет разной.
 */
export function contactInitial(name: string): string {
  return (name.replace(/^@+/, '').trim() || name.trim() || '?').slice(0, 1).toUpperCase();
}

export function contactTitle(c: Pick<ContactLine, 'name' | 'username'>, noName = ''): string {
  // Технический id (сессия чата на сайте — длинный UUID) в подпись НЕ идёт: владелец
  // 2026-08-02 увидел такие «имена» в списке. Человек не представился — так и пишем.
  // КОРОТКОЕ ИМЯ — ТЕМ ЖЕ ПРАВИЛОМ, ЧТО В СПИСКЕ ЧАТОВ (владелец 15.09.2026: «не клонируй,
  // сделай общим»): длинное имя режется по границе титула, а не влезло — логин
  // (`shortName`, lib/display-name.ts — единственное место правила). Полное имя карточка
  // показывает второй строкой (fullNameNote).
  return shortName(c.name, c.username ? `@${c.username}` : '') || noName;
}

/** Отбор списка контактов — общий для раздела и второй колонки меню. Канал и аккаунт —
 *  отбор кружка-фильтра (владелец 2026-08-04), 'all' = не отбираем. */
/**
 * МЕТКИ ЧЕЛОВЕКА — ВСЁ, ЧТО НАЗНАЧИЛ КЛИЕНТ (владелец 13.09.2026, БЭКЛОГ п.13): рукой на
 * карточке, кнопкой «Поставить метку» в письме, шагом «Записать в карточку». `ContactRow.tags`
 * приходит из колонки `tags`, где лежит ровно это. Служебные отметки системы («прошёл
 * бота», «дошёл до цели», защита ИГ-шаблона) живут отдельной колонкой `marks`, метками не
 * являются и на экраны кабинета не попадают вовсе. Детектор `isServiceMark` ОДИН на
 * продукт (@scriptera/db) и работает замком: служебное не пройдёт в метки даже опечаткой.
 */

/** Метка словами. Служебную прохождения бота читаем словами, остальные идут как есть. */
export function contactTagLabel(tag: string, locale: string): string {
  return chainTagWords(tag, locale === 'en' ? 'en' : 'ru');
}

export function filterContacts(
  contacts: ContactRow[],
  q: string,
  tag: string,
  channel = 'all',
  account = 'all',
): ContactRow[] {
  const needle = q.trim().toLowerCase();
  return contacts.filter((c) => {
    if (tag !== 'all' && !c.tags.includes(tag)) return false;
    if (channel !== 'all' && c.channel !== channel) return false;
    if (account !== 'all' && c.botId !== account) return false;
    if (!needle) return true;
    return [c.name, c.username, c.phone, c.email, c.externalId, ...c.tags]
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });
}

// СВОДА МЕТОК ПО ПОКАЗАННЫМ ЛЮДЯМ БОЛЬШЕ НЕТ (SCALE-PLAN Е2): список приезжает
// страницами, и считать по нему метки значило бы показывать только те, что попались на
// первой полусотне. Свод берётся у базы одним запросом — ручка `/api/cabinet/contact-tags`
// (её же читает карточка человека); второго списка меток в кабинете не заводить.

/** Кружок человека в списке (владелец 2026-08-04): ФОТО из профиля мессенджера — та же
 *  ссылка нашего бакета и тот же класс .msg-av, что в «Сообщениях» (аватар добывает
 *  приёмник входящих и кладёт в карточку контакта, второго учёта нет). Фото нет или оно
 *  не открылось — прежняя плитка канала. */
function ContactAvatar({ contact, locale }: { contact: ContactLine; locale: string }) {
  const [broken, setBroken] = useState(false);
  if (contact.avatarUrl && !broken) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- картинка человека из нашего бакета, next/image не нужен
      <img
        className="msg-av ct-ava"
        src={contact.avatarUrl}
        alt=""
        width={40}
        height={40}
        decoding="async"
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span
      className={`ct-ava scn-ava-tile scn-ava-${contact.channel}`}
      title={channelLabel(contact.channel, locale)}
      aria-hidden
    >
      <ChannelIcon channel={contact.channel} size={18} />
    </span>
  );
}

/**
 * СПИСОК ЛЮДЕЙ — один ряд на весь кабинет (ТЗ 11 пакета «Рассылки»): раздел «Контакты» и
 * список «кто получит рассылку» показывают человека ОДИНАКОВО, близнеца ряда не рисуют.
 * На широком экране — колонки Имя · Связь · Метки · Был(а) (шапка .ct-head), на узком CSS
 * складывает ряд в две строки и прячет колонки связи и даты (мобильный вид — не таблица).
 * Отступ снизу и прокрутка широких колонок — в CSS (.ct-table), своих чисел тут нет.
 * `onOpen` не задан — ряд просто показывает человека и не нажимается.
 *
 * `pick` — РЕЖИМ ВЫБОРА экрана «Кто получит» (MESSAGE-COMPOSER И3): слева у ряда встаёт
 * галочка набора, тап по ряду её переключает. Второго списка людей под выбор не заводим —
 * это тот же ряд, только с колонкой галочки (её добавляет CSS-модификатор `is-pick`).
 */
export function ContactTable<T extends ContactLine>({
  contacts,
  onOpen,
  onWarm,
  pick,
  shape,
}: {
  /** Имя формы экрана (ред. 9, 7.4.2): таблица — корень `data-shape`, с неё снимается форма для заглушки. */
  shape?: string;
  contacts: readonly T[];
  onOpen?: (c: T) => void;
  /** Касание/наведение строки — прогреть карточку до открытия (PREFETCH-TZ §2.3). */
  onWarm?: (c: T) => void;
  pick?: {
    /** Подпись галочки для скринридера («Выбрать человека»). */
    label: string;
    checked: (c: T) => boolean;
    onToggle: (c: T) => void;
  };
}) {
  const t = useTranslations('cabinet.contacts');
  const locale = useLocale();
  const fmtDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '—';
  return (
    <div className={`card ct-table${pick ? ' is-pick' : ''}`} data-shape={shape}>
      <div className="ct-head meta" aria-hidden>
        {pick ? <span /> : null}
        <span />
        <span>{t('colName')}</span>
        <span>{t('colReach')}</span>
        <span>{t('colTags')}</span>
        <span style={{ textAlign: 'right' }}>{t('colSeen')}</span>
      </div>
      {contacts.map((c) => (
        <div
          key={c.id}
          className="ct-row"
          // Прогрев видимых строк (PREFETCH-CAREFUL §2 п.1) — только у списка, который греет карточки.
          {...(onWarm && !pick ? { 'data-warm-id': c.id } : {})}
          {...(pick
            ? {
                role: 'button',
                tabIndex: 0,
                onClick: () => pick.onToggle(c),
                onKeyDown: (e: ReactKeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') pick.onToggle(c);
                },
              }
            : onOpen
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => onOpen(c),
                  ...(onWarm
                    ? { onPointerDown: () => onWarm(c), onMouseEnter: () => onWarm(c) }
                    : {}),
                  onKeyDown: (e: ReactKeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') onOpen(c);
                  },
                }
              : {})}
        >
          {pick ? (
            <KCheckbox
              checked={pick.checked(c)}
              ariaLabel={pick.label}
              onChange={() => pick.onToggle(c)}
            />
          ) : null}
          {/* Лицо человека, а без фото — плитка канала (фирменный цвет, те же классы,
              что у карточек «Скриптов»): видно, откуда он пришёл. */}
          <ContactAvatar contact={c} locale={locale} />
          <span className="ct-name-cell">
            <span className="ct-name">{contactTitle(c, t('noName'))}</span>
            {/* Имени нет и заголовок уже «@ник» — второй строкой ник не повторяем (кадр iOS
                K13-contacts-all: «@ksenyalee» одной строкой). */}
            {c.username &&
            contactTitle(c, t('noName')).replace(/^@+/, '') !== c.username.replace(/^@/, '') ? (
              <span className="meta ct-login">@{c.username.replace(/^@/, '')}</span>
            ) : null}
          </span>
          <span className="ct-cell-reach meta">{c.phone || c.email || '—'}</span>
          <span className="ct-cell-tags">
            {c.tags.length > 0 ? (
              c.tags.map((tg) => (
                <span key={tg} className="ct-tag">
                  {contactTagLabel(tg, locale)}
                </span>
              ))
            ) : (
              <span className="meta">{t('noTags')}</span>
            )}
          </span>
          <span className="ct-cell-when meta">{fmtDate(c.lastSeenAt || c.createdAt)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * ЧТО ЧЕЛОВЕК ПРОШЁЛ И ПОЛУЧАЛ (владелец 15.09.2026) — ОДИН запрос на родной лист и
 * веб-карточку. Пустой id — запроса нет (переписка без карточки).
 */
/** Свежая строка человека по id — ключ и адрес те же, что у прогрева карточки (PREFETCH-TZ §2.3); пусто — ещё не
 *  приехала или человека нет. */
function useFreshContact(contactId: string): ContactRow | null {
  const { data } = usePersistentData<ContactsResp>(
    contactId ? `cabinet:contact:${contactId}` : '',
    contactId ? `${CONTACTS_URL}?id=${encodeURIComponent(contactId)}` : '',
    { accept: acceptList },
  );
  return data?.ok ? (data.contacts?.[0] ?? null) : null;
}

function useContactHistory(contactId: string) {
  return usePersistentData<{
    ok: boolean;
    bots: ContactHistoryBotRow[];
    letters: ContactHistoryLetterRow[];
  }>(
    contactId ? `cabinet:contact-history:${contactId}` : '',
    contactId ? `/api/cabinet/contacts/history?id=${contactId}` : '',
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
}

/**
 * ПРОГРЕТЬ КАРТОЧКУ ЧЕЛОВЕКА, ПОКА ЕЁ НЕ ОТКРЫЛИ (PREFETCH-TZ §2.3, правило 2 владельца 10.10.2026):
 * история, прогноз цепочек и переписка для кружка «Открыть переписку» — ТЕМИ ЖЕ ключами и адресами,
 * что читает сама карточка (useContactHistory, ChainNextRows / прогноз родного листа, ContactChatLookup);
 * метки — общий ключ раздела. Объединённой ручки нет — три запроса очередью обёртки прогрева.
 * `urgent` — касание/наведение строки: мимо очереди, все четыре; фон со списка — только история.
 */
export function warmContactCard(c: { id: string; dialogId?: string | null }, urgent = false): void {
  if (!c.id) return;
  const ok = { accept: (raw: unknown) => !!(raw as { ok?: boolean })?.ok };
  warmTarget(
    'contacts.card',
    `cabinet:contact-history:${c.id}`,
    `/api/cabinet/contacts/history?id=${c.id}`,
    ok,
    urgent,
  );
  // ФОН СО СПИСКА — ТОЛЬКО ИСТОРИЯ (PREFETCH-CAREFUL-TZ §5, владелец 11.10.2026: «аккуратный прогрев»): четыре
  // запроса на человека на каждый видимый ряд — лишняя нагрузка; прогноз, переписка и метки — по касанию строки.
  if (!urgent) return;
  if (!CHAINS_HIDDEN) {
    warmTarget(
      'contacts.card',
      `cabinet:chain-forecast:${c.id}`,
      `/api/cabinet/chain-forecast?contact=${c.id}`,
      ok,
      urgent,
    );
  }
  if (!c.dialogId)
    warmTarget(
      'contacts.card',
      `cabinet:contact-dialog:${c.id}`,
      `/api/cabinet/contacts/${c.id}/dialog`,
      ok,
      urgent,
    );
  warmTarget('contacts.card', 'cabinet:contact-tags', '/api/cabinet/contact-tags', ok, urgent);
}

/** Слова и короткая дата журнала человека (П195) — одни на лист и веб-карточку. */
function useContactHistoryWords(): ContactCardRowsWords {
  const t = useTranslations('cabinet.contacts');
  const locale = useLocale();
  return {
    botsPassed: t('botsPassed'),
    botsNone: t('botsNone'),
    botGone: t('botGone'),
    lettersGot: t('lettersGot'),
    lettersNone: t('lettersNone'),
    letterBcast: t('letterBcast'),
    letterN: (n) => t('letterN', { n }),
    // Пометка недоставленного (П198) — те же слова, что в «Истории сообщений» цепочек.
    letterMark: (status, reason) => {
      if (status === 'failed') return t('chainStFailed');
      if (status === 'cancelled') return t('chainStCancelled');
      if (status !== 'skipped') return '';
      if (reason === 'filter') return t('chainStSkipFilter');
      if (reason === 'paused') return t('chainStSkipPaused');
      if (reason === 'ended') return t('chainStSkipEnded');
      return t('chainStSkip');
    },
    more: (n) => t('lettersMore', { n }),
    when: (iso) =>
      msgShortWhen(iso, locale, {
        today: (time) => t('whenToday', { time }),
        yesterday: (time) => t('whenYesterday', { time }),
      }),
  };
}

/**
 * «ПРОШЁЛ БОТОВ» И «ПОЛУЧАЛ СООБЩЕНИЯ» В БРАУЗЕРЕ (П195): те же группы, что уходят
 * родному листу (`contactHistoryGroups`), нарисованные набором — список строк с датой
 * справа, «Ещё N» разворачивает группу бота.
 */
function ContactHistoryBlock({ contactId }: { contactId: string }) {
  const history = useContactHistory(contactId);
  const words = useContactHistoryWords();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  // ПОКА ИСТОРИИ НЕТ — КАРКАС ТОЙ ЖЕ ГЕОМЕТРИИ (правило 1 владельца 10.10.2026, PREFETCH-TZ §2.3): обе
  // группы с подписями и строкой «пусто», невидимые (sk-hold). Было — `null`, и блок «вырастал».
  const groups = contactHistoryGroups(history.data ?? { bots: [], letters: [] }, words, expanded);
  return (
    <div className={history.data ? 'ct-history' : 'ct-history sk-hold'} aria-busy={!history.data}>
      {groups.map((g) =>
        g.fields.length === 0 ? (
          // Пустая группа — подпись раздела набора и одна сноска (кадр iOS K13-contact-card: «Прошёл ботов» серым
          // мелким, под ним «Ботов ещё не проходил»; TARIFF-PAGE-TZ §11 П8 — было жирной подписью формы).
          <KListSection key={g.key} label={g.label}>
            <FHint>{g.hint}</FHint>
          </KListSection>
        ) : (
          <KListSection key={g.key} label={g.label}>
            <KListGroup>
              {g.fields.map((r) =>
                r.kind === 'button' ? (
                  <KListRow
                    key={r.key}
                    tone="accent"
                    title={r.title}
                    onClick={() => {
                      const id = r.key.slice(CT_MORE.length);
                      setExpanded((prev) => new Set([...prev, id]));
                    }}
                  />
                ) : (
                  <KListRow key={r.key} title={r.title} value={r.value} />
                ),
              )}
            </KListGroup>
          </KListSection>
        ),
      )}
    </div>
  );
}

/**
 * «ЧТО ЧЕЛОВЕК ПОЛУЧИТ ДАЛЬШЕ И КОГДА» на карточке контакта. Второй продукт выключен
 * (CHAINS_HIDDEN) — не спрашиваем вовсе: цепочек всё равно ни у кого нет.
 */
export function ContactChainNext({ contactId }: { contactId: string }) {
  if (CHAINS_HIDDEN) return null;
  return <ChainNextRows contactId={contactId} />;
}

function ChainNextRows({ contactId }: { contactId: string }) {
  const t = useTranslations('cabinet.contacts');
  const locale = useLocale();
  // Слова условий — общая рука со строкой «Кто получит» и подстрокой сообщения (И3).
  const words = useAudienceWords();
  const { data, reload } = usePersistentData<ChainForecastResp>(
    `cabinet:chain-forecast:${contactId}`,
    `/api/cabinet/chain-forecast?contact=${contactId}`,
    { accept: (raw) => !!(raw as ChainForecastResp)?.ok },
  );
  /** Что человек пропустил ПРЯМО СЕЙЧАС: строка остаётся на месте с пометкой «пропущено
   *  вами», пока список не перечитан, — исчезнувшая строка выглядела бы сбоем. */
  const [skipped, setSkipped] = useState<string[]>([]);
  /** Цепочки, из которых человека убрали прямо сейчас: до перечитывания блок молчит. */
  const [removed, setRemoved] = useState<string[]>([]);
  const chains = data?.chains ?? [];
  const stopped = data?.stopped ?? [];
  const history = data?.history ?? [];
  const when = (iso: string) =>
    new Date(iso).toLocaleString(locale, {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  /**
   * ВЫБЫТИЕ СЛОВАМИ (И2): «выйдет из цепочки на сообщении 3: есть метка «купил»». Условия
   * складывает ТА ЖЕ функция, что строку формы и подстроку сообщения (audienceWords) —
   * свои имена ботов, сообщений и меток знает экран, а не ручка. Номера сообщения или
   * условий нет — говорим общей строкой, а не молчим.
   */
  const stopHere = (ord: number, why: readonly AudienceValue[]): string => {
    const said = why
      .map((f) => words(f))
      .filter((x) => x !== '')
      .join(' · ');
    return ord > 0 && said !== '' ? t('chainStopAt', { n: ord, why: said }) : t('chainStopHere');
  };

  const stopWords = (reason: string): string => chainStopWords(reason, t);

  /**
   * СУДЬБА СООБЩЕНИЯ СЛОВАМИ (К17.4 п.2). Причину пропуска называет сервер коротким
   * ключом, человеческую фразу собирает экран — язык кабинета меняется на месте, и
   * записанная строка осталась бы на прежнем языке навсегда (то же правило, что у
   * событий переписки).
   */
  const sendWords = (status: string, reason: string): string => {
    if (status === 'sent') return t('chainStSent');
    if (status === 'failed') return t('chainStFailed');
    if (status === 'cancelled') return t('chainStCancelled');
    if (status === 'planned') return t('chainStPlanned');
    if (status !== 'skipped') return '';
    if (reason === 'filter') return t('chainStSkipFilter');
    if (reason === 'paused') return t('chainStSkipPaused');
    if (reason === 'ended') return t('chainStSkipEnded');
    return t('chainStSkip');
  };

  /** Вернуть человека в цепочку, с которой его сняли (по одной, ТЗ В4). */
  const resume = async (memberId: string) => {
    const res = await fetch('/api/cabinet/chain-forecast', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ memberId }),
    }).catch(() => null);
    if (res?.ok) reload();
  };

  /**
   * ПРОПУСТИТЬ ОДНО ПРЕДСТОЯЩЕЕ СООБЩЕНИЕ (К15 П9 п.4, слово владельца 06.09: кнопка
   * называется «Пропустить» — сообщение НЕ уходит, а следующее идёт по своему сроку;
   * заново код его не планирует). Строка остаётся на месте с пометкой «пропущено вами» —
   * так видно, что нажатие сработало. Ручка прежняя (`cancel: sendId`).
   */
  const skipSend = async (sendId: string) => {
    setSkipped((was) => (was.includes(sendId) ? was : [...was, sendId]));
    const res = await fetch('/api/cabinet/chain-forecast', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ cancel: sendId }),
    }).catch(() => null);
    if (res?.ok) reload();
  };

  /**
   * УБРАТЬ ЧЕЛОВЕКА ИЗ ЦЕПОЧКИ ЦЕЛИКОМ (слово владельца 06.09). Дальше блок этой цепочки
   * встаёт в «Снят с цепочек» с причиной «вы убрали» и кнопкой «Вернуть» — ровно так же,
   * как у остальных причин снятия. Прогон уже не идёт (409 not_running) — перечитываем
   * список молча: человек и так увидит верное состояние.
   */
  const removeFromChain = async (memberId: string) => {
    setRemoved((was) => (was.includes(memberId) ? was : [...was, memberId]));
    const res = await fetch('/api/cabinet/chain-forecast', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ stop: memberId }),
    }).catch(() => null);
    if (res) reload();
  };

  const letterText = (l: { text: string; photoOnly: boolean }) =>
    l.text || (l.photoOnly ? t('chainPhoto') : t('chainNoText'));

  if (chains.length === 0 && stopped.length === 0 && history.length === 0) return null;
  return (
    <>
      {chains
        .filter((c) => !(c.memberId && removed.includes(c.memberId)))
        .map((c) => (
          <VGroup key={c.chainId} label={`${t('chainUpcoming')} · ${c.title}`}>
            {/* ЖДЁТ КОНЦА РАЗГОВОРА С БОТОМ (BACKLOG п.21): вход сработал под замком другого
              бота; письма пойдут, когда тот разговор закончится (молчание дольше срока
              бота или бота выключили). Срок неизвестен — строка без даты. */}
            {c.waitingFor ? (
              <DKV key={`${c.chainId}-wait`} label="" last={!c.memberId}>
                {c.waitingFor.title
                  ? t('chainWaitingFor', { name: c.waitingFor.title })
                  : t('chainWaitingForBot')}
              </DKV>
            ) : null}
            {c.letters.map((l, i, all) => (
              <DKV
                key={`${c.chainId}-${i}`}
                label={when(l.at)}
                last={i === all.length - 1 && !c.stopAt && !c.memberId}
              >
                {/* «Пропустить» — та же маленькая кнопка набора, что «Вернуть» у снятых.
                  Строки без своей записи в журнале плана (старый ответ ручки) пропускать
                  нечем — тогда стоит один текст, как раньше. */}
                {l.sendId ? (
                  <span className="ct-resume">
                    {letterText(l)}
                    {skipped.includes(l.sendId) ? (
                      <span className="meta">{t('chainSkippedMark')}</span>
                    ) : (
                      <KButton size="sm" onClick={() => void skipSend(l.sendId!)}>
                        {t('chainSkipBtn')}
                      </KButton>
                    )}
                  </span>
                ) : (
                  letterText(l)
                )}
              </DKV>
            ))}
            {/* ВЫБЫТИЕ (И2): на этом сообщении человек перестанет подходить под условия
              цепочки, и сообщений дальше не будет. Считает это та же проверка, что и
              отправку, — база. */}
            {c.stopAt ? (
              <DKV key={`${c.chainId}-stop`} label={when(c.stopAt)} last={!c.memberId}>
                {stopHere(c.stopOrd ?? 0, c.stopWhy ?? [])}
              </DKV>
            ) : null}
            {/* УБРАТЬ ИЗ ЦЕПОЧКИ ЦЕЛИКОМ (слово владельца 06.09) — последней строкой блока:
              это про всю цепочку, а не про одно сообщение. Кнопка та же, что «Вернуть». */}
            {c.memberId ? (
              <DKV key={`${c.chainId}-off`} label="" last>
                <span className="ct-resume">
                  <KButton size="sm" onClick={() => void removeFromChain(c.memberId!)}>
                    {t('chainRemove')}
                  </KButton>
                </span>
              </DKV>
            ) : null}
          </VGroup>
        ))}
      {stopped.length > 0 && (
        <VGroup label={t('chainStopped')}>
          {stopped.map((sRow, i, all) => (
            <DKV key={sRow.memberId} label={sRow.title} last={i === all.length - 1}>
              <span className="ct-resume">
                {stopWords(sRow.reason)}
                <KButton size="sm" onClick={() => void resume(sRow.memberId)}>
                  {t('chainResume')}
                </KButton>
              </span>
            </DKV>
          ))}
        </VGroup>
      )}
      {/* ИСТОРИЯ КАЖДОГО СООБЩЕНИЯ (К15 П9 п.3): что ушло, что нет и почему. Одним
          списком по всем цепочкам — имя цепочки стоит в самой строке. */}
      {history.length > 0 && (
        <VGroup label={t('chainHistory')}>
          {history.map((h, i, all) => (
            <DKV key={h.sendId} label={when(h.at)} last={i === all.length - 1}>
              {[h.title, h.text, sendWords(h.status, h.reason ?? '')].filter(Boolean).join(' · ')}
            </DKV>
          ))}
        </VGroup>
      )}
    </>
  );
}

/** Телефон в браузере — карточка человека рисуется как родной экран (ТЗ WEB-PARITY-3). */
const phoneWebNow = (): boolean => readPhoneLook();

/** Строка карточки, которую добавляет зовущий (переписка: окно ответа, «Отвечает»). */
export interface ContactCardIosRow {
  key: string;
  title: ReactNode;
  value: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
}

/**
 * КАРТОЧКА ЧЕЛОВЕКА КАК В iOS (ТЗ WEB-PARITY-3 Ч27/К25, кадры C09-chat-info /
 * K13-contact-card): аватар 120, имя 28 и ник по центру; карточка строк «подпись ·
 * значение серым» (у переписки сверху свои строки — окно ответа, канал, аккаунт,
 * «Отвечает · Бот ›»); подсказка снаружи; группа «Активность»; «Метки» — белая кнопка
 * «Настроить метки» → лист (ContactTagsSheet); группы истории («Прошёл ботов», «Получал
 * сообщения», «Что придёт дальше»); внизу белая «Удалить контакт» красным — через окно
 * вопроса, удаление — пометкой на сервере (запрет 4а). ОДНА карточка на переписку и на
 * раздел «Контакты» — второй разметки нет.
 */
export function ContactCardIos({
  contact,
  dialogId,
  name,
  username,
  avatarUrl,
  channel,
  account,
  rows = [],
  hint,
  onDeleted,
  onSaved,
}: {
  /** Человек; null — карточки ещё нет (переписка без записи), тогда нет активности и удаления. */
  contact: ContactRow | null;
  dialogId?: string;
  name: string;
  username: string;
  avatarUrl: string;
  channel: string;
  /** Аккаунт клиента, через который человек достижим (@логин или имя); пусто — строки нет. */
  account?: string;
  /** Строки зовущего ПЕРЕД «Канал/Аккаунт» (окно ответа) и ПОСЛЕ («Отвечает»). */
  rows?: readonly ContactCardIosRow[];
  hint?: ReactNode;
  onDeleted?: () => void;
  onSaved?: () => void;
}) {
  const t = useTranslations('cabinet.contacts');
  const tc = useTranslations('cabinet');
  const tm = useTranslations('cabinet.messages');
  const locale = useLocale();
  const panel = useSlidePanel();
  const [busy, setBusy] = useState(false);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  // Канал приходит и коротким ключом переписки (tg/ig), и полным именем карточки.
  const chKey = channel === 'tg' ? 'telegram' : channel === 'ig' ? 'instagram' : channel;
  const chName = channelLabel(chKey, locale);
  // «Не писать» — строкой с тумблером (TARIFF-PAGE-TZ §11 П8): переключается сразу, отдельной записью; не
  // прошло — тумблер возвращается (врать про состояние нельзя).
  const [quiet, setQuiet] = useState(contact?.doNotDisturb === true);
  // СВЕЖАЯ СТРОКА ЧЕЛОВЕКА (TARIFF-PAGE-TZ §11 П8): карточка по адресу встаёт из кэша списка, а свежий список может
  // приехать уже после — тумблер и поля показали бы прежние значения. Та же ручка и тот же ключ, что у прогрева
  // (`cabinet:contact:<id>`): приехало новое — состояние подтягивается, пока человек его сам не трогал.
  const fresh = useFreshContact(contact?.id ?? '');
  const quietTouched = useRef(false);
  useEffect(() => {
    if (fresh && !quietTouched.current) setQuiet(fresh.doNotDisturb === true);
  }, [fresh]);
  const saveQuiet = async (on: boolean) => {
    if (!contact) return;
    quietTouched.current = true;
    setQuiet(on);
    const res = await fetch('/api/cabinet/contacts', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: contact.id, doNotDisturb: on }),
    }).catch(() => null);
    if (!res || !res.ok) setQuiet(!on);
    else onSaved?.();
  };
  // Правка имени, телефона и почты — листом стопкой с «Готово» в шапке (как редактор папки).
  const openEdit = () => {
    if (!contact) return;
    panel.open({
      title: t('edit'),
      sheet: true,
      deck: true,
      winKey: 'contact-edit',
      body: <ContactEditSheet contact={fresh ?? contact} onSaved={onSaved} />,
    });
  };
  const openTags = () =>
    panel.open({
      title: t('fldTags'),
      sheet: true,
      deck: true,
      // Полулист (кадр C09-chat-tags: родной лист «Метки» встаёт до середины экрана).
      ...(phoneWebNow() ? { sheetDetent: 0.5 } : {}),
      winKey: 'contact-tags',
      actionIcon: 'checkmark',
      actionLabel: tc('done'),
      onAction: () => panel.back(),
      body: (
        <ContactTagsSheet
          contact={contact}
          {...(dialogId ? { dialogId } : {})}
          {...(onSaved ? { onSaved } : {})}
        />
      ),
    });
  const del = async () => {
    if (!contact || busy) return;
    const yes = await askConfirm({ question: t('deleteSure'), confirmLabel: t('delete') });
    if (!yes) return;
    setBusy(true);
    try {
      const res = await fetch('/api/cabinet/contacts', {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id: contact.id }),
      });
      if (res.ok) onDeleted?.();
    } finally {
      setBusy(false);
    }
  };
  const before = rows.filter((r) => r.key !== 'answers');
  const after = rows.filter((r) => r.key === 'answers');
  return (
    <div className="ct-ios">
      <div className="ct-ios-hero">
        {avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element -- фото человека из нашего бакета
          <img className="ct-ios-av" src={avatarUrl} alt="" width={88} height={88} decoding="async" />
        ) : (
          <div className="ct-ios-av ct-ios-avph" style={{ color: circleColor(name || username) }}>
            {contactInitial(name || username)}
          </div>
        )}
        <h1 className="ct-ios-h1">{name || (username ? `@${username}` : t('noName'))}</h1>
        {username ? <div className="ct-ios-nick">@{username}</div> : null}
      </div>
      {contact?.stoppedAt && <DWarn>{t('stopped', { date: fmtDate(contact.stoppedAt) })}</DWarn>}
      <KListGroup>
        {before.map((r) => (
          <KListRow
            key={r.key}
            title={r.title}
            value={r.value}
            {...(r.chevron ? { chevron: true } : {})}
            {...(r.onClick ? { onClick: r.onClick } : {})}
          />
        ))}
        <KListRow
          title={t('fldChannel')}
          value={
            <span className="ct-ios-chan">
              {/* Значок сети — только у карточки из переписки (у родной строка «Канал» несёт
                  значок, когда карточку открыли из «Чатов»); из «Контактов» — одно слово
                  (кадр iOS K13-contact-card: «Канал · Instagram»). */}
              {/* Знак — фирменным цветом сети (кадр C09-chat-info: розовый знак Instagram). */}
              {dialogId ? (
                <span className="ct-ios-chan-ic" style={{ color: channelHex(chKey) }}>
                  <ChannelIcon channel={chKey} size={14} />
                </span>
              ) : null}
              {chName}
            </span>
          }
        />
        {account ? <KListRow title={tm('infoAccount')} value={account} /> : null}
        {after.map((r) => (
          <KListRow
            key={r.key}
            title={r.title}
            value={r.value}
            {...(r.chevron ? { chevron: true } : {})}
            {...(r.onClick ? { onClick: r.onClick } : {})}
          />
        ))}
      </KListGroup>
      {hint ? <FHint className="ct-ios-hint ct-ios-hint--card">{hint}</FHint> : null}
      {contact ? (
        <KListSection label={t('activity')}>
          <KListGroup>
            {/* «Последний раз» — как в родной карточке (владелец 10.09: «Был(а)» — неправда; кадр K13-contact-card). */}
            <KListRow title={t('fldLast')} value={fmtDate(contact.lastSeenAt)} />
            <KListRow title={t('fldSince')} value={fmtDate(contact.createdAt)} />
          </KListGroup>
        </KListSection>
      ) : null}
      <KListSection label={t('fldTags')}>
        <KListGroup>
          <KListRow title={t('tagsSetup')} tone="accent" onClick={openTags} />
        </KListGroup>
      </KListSection>
      {contact ? (
        <>
          {/* «Не писать» — строка с тумблером и сноской (прежний веб-вариант: тумблер без строки под заголовком). */}
          <KListSection label={t('quietGroup')} hint={t('quietHint')}>
            <KListGroup>
              <KListRow
                title={t('quiet')}
                control
                value={
                  <KSwitch
                    ariaLabel={t('quiet')}
                    checked={quiet}
                    onChange={(on) => void saveQuiet(on)}
                  />
                }
              />
            </KListGroup>
          </KListSection>
          {/* Переменные бота по человеку (имя, выбор, ответы) — как было в веб-карточке, строками набора. */}
          {Object.keys(contact.vars).length > 0 && (
            <KListSection label={t('fldVars')}>
              <KListGroup>
                {Object.entries(contact.vars).map(([k, v]) => (
                  <KListRow key={k} className="k-lrow--wrap" title={k} value={v} />
                ))}
              </KListGroup>
            </KListSection>
          )}
          <ContactHistoryBlock contactId={contact.id} />
          <ContactChainNext contactId={contact.id} />
          {/* «Изменить» — действие во всю ширину над «Удалить контакт» (TARIFF-PAGE-TZ §11 П8; капсула удаления —
              кадр iOS K13-contact-card: белая капсула с красным словом). */}
          <KButton action tone="primary" onClick={openEdit}>
            {t('edit')}
          </KButton>
          <KButton
            action
            className="prof-wide-btn ct-ios-del"
            disabled={busy}
            onClick={() => void del()}
          >
            {t('delete')}
          </KButton>
        </>
      ) : null}
    </div>
  );
}

/** ПРАВКА ДАННЫХ ЧЕЛОВЕКА (имя, телефон, почта; канал, id и ник ведут сценарии — только показываются):
 *  строки набора с полем, «Готово» в шапке листа — как у редактора папки (contact-folders.tsx). Метки сюда
 *  не входят — они правятся своим листом сразу. */
function ContactEditSheet({ contact, onSaved }: { contact: ContactRow; onSaved?: () => void }) {
  const t = useTranslations('cabinet.contacts');
  const tc = useTranslations('cabinet');
  const panel = useSlidePanel();
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone);
  const [email, setEmail] = useState(contact.email);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  // Поля — из свежей строки, пока их не трогали (карточка могла открыться из кэша списка, см. useFreshContact).
  const fresh = useFreshContact(contact.id);
  const dirty = useRef(false);
  useEffect(() => {
    if (!fresh || dirty.current) return;
    setName(fresh.name);
    setPhone(fresh.phone);
    setEmail(fresh.email);
  }, [fresh]);
  const saveRef = useRef<() => void>(() => {});
  saveRef.current = () => {
    if (busy) return;
    void (async () => {
      setBusy(true);
      setErr(false);
      try {
        const res = await fetch('/api/cabinet/contacts', {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            id: contact.id,
            name: name.trim(),
            phone: phone.trim(),
            email: email.trim(),
          }),
        });
        if (!res.ok) {
          setErr(true);
          return;
        }
        onSaved?.();
        panel.back();
      } finally {
        setBusy(false);
      }
    })();
  };
  useEffect(() => {
    panel.update({ actionLabel: tc('done'), onAction: () => saveRef.current() });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- кнопка шапки ставится один раз, руку держит ref
  }, []);
  return (
    <div className="ct-ios">
      {err && <FFormError>{t('saveError')}</FFormError>}
      <KListGroup>
        <KListRow
          className="cf-field-row"
          title={t('fldName')}
          control
          value={
            <KInput
              value={name}
              maxLength={250}
              aria-label={t('fldName')}
              onChange={(e) => {
                dirty.current = true;
                setName(e.target.value);
              }}
            />
          }
        />
        <KListRow
          className="cf-field-row"
          title={t('fldPhone')}
          control
          value={
            <KInput
              value={phone}
              maxLength={250}
              aria-label={t('fldPhone')}
              onChange={(e) => {
                dirty.current = true;
                setPhone(e.target.value);
              }}
            />
          }
        />
        <KListRow
          className="cf-field-row"
          title={t('fldEmail')}
          control
          value={
            <KInput
              value={email}
              maxLength={250}
              aria-label={t('fldEmail')}
              onChange={(e) => {
                dirty.current = true;
                setEmail(e.target.value);
              }}
            />
          }
        />
      </KListGroup>
    </div>
  );
}

/**
 * ЛИСТ «МЕТКИ» (ТЗ WEB-PARITY-3 Ч28, кадр iOS C09-chat-tags): карточка со строкой
 * «Добавить метку» акцентом (окно-вопрос «Новая метка») и строками меток с «×» справа
 * (в iOS — смахивание влево; на сайте по канону крестик), «Меток пока нет.», подсказка
 * «Метки общие для всех пользователей…». Запись — той же рукой, что у строки меток
 * (putContactTags), сразу по действию.
 */
export function ContactTagsSheet({
  contact,
  dialogId,
  onSaved,
}: {
  contact: ContactRow | null;
  dialogId?: string;
  onSaved?: () => void;
}) {
  const t = useTranslations('cabinet.contacts');
  const locale = useLocale();
  const [tags, setTags] = useState<string[]>(contact?.tags ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const put = (next: string[]) => {
    const was = tags;
    setTags(next);
    setBusy(true);
    setErr(false);
    void (async () => {
      const ok = await putContactTags({ contact, dialogId, tags: next });
      if (ok) onSaved?.();
      else {
        setTags(was);
        setErr(true);
      }
      setBusy(false);
    })();
  };
  const add = async () => {
    const raw = await askPrompt({
      title: t('tagsAdd'),
      placeholder: t('tagsAddPh'),
      confirmLabel: t('tagsAddYes'),
    });
    const name = (raw ?? '').trim();
    if (!name || isServiceMark(name) || tags.includes(name)) return;
    put([...tags, name]);
  };
  return (
    <div className="ct-ios ct-ios-tags">
      {err && <FFormError>{t('saveError')}</FFormError>}
      <KListGroup>
        <KListRow title={t('tagsAdd')} tone="accent" disabled={busy} onClick={() => void add()} />
        {tags.map((tg) => (
          <FSwipeRow
            key={tg}
            deleteLabel={t('tagsRemove')}
            onDelete={() => put(tags.filter((x) => x !== tg))}
          >
            <KListRow title={contactTagLabel(tg, locale)} />
          </FSwipeRow>
        ))}
      </KListGroup>
      {tags.length === 0 ? <FHint className="ct-ios-hint">{t('tagsEmpty')}</FHint> : null}
      <FHint className="ct-ios-hint ct-ios-note">{t('tagsSharedNote')}</FHint>
    </div>
  );
}

/** Кружок «Открыть переписку» шапки карточки (кадр iOS K13-contact-card). Адрес переписки —
 *  из строки списка; нет его — ручка `GET /api/cabinet/contacts/<id>/dialog` (дополнение 356
 *  ТЗ WEB-CAUSES: переписка, заведённая раньше карточки, с ней не связана). Переписки нет —
 *  кружка нет. */
function ContactChatBtn({
  contactId,
  dialogId,
  onOpen,
}: {
  contactId: string;
  dialogId: string;
  onOpen: (id: string) => void;
}) {
  if (dialogId) return <ContactChatCircle onClick={() => onOpen(dialogId)} />;
  return <ContactChatLookup contactId={contactId} onOpen={onOpen} />;
}

function ContactChatLookup({
  contactId,
  onOpen,
}: {
  contactId: string;
  onOpen: (id: string) => void;
}) {
  const { data } = usePersistentData<{ ok: boolean; dialogId?: string }>(
    `cabinet:contact-dialog:${contactId}`,
    `/api/cabinet/contacts/${contactId}/dialog`,
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const id = data?.dialogId ?? '';
  return id ? <ContactChatCircle onClick={() => onOpen(id)} /> : null;
}

function ContactChatCircle({ onClick }: { onClick: () => void }) {
  const t = useTranslations('cabinet.contacts');
  return (
    <KBareButton
      className="dr-info"
      aria-label={t('openChat')}
      title={t('openChat')}
      onClick={onClick}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M5 4.5h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H10l-4.5 3.5V17.5H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" />
      </svg>
    </KBareButton>
  );
}

/** КАРТОЧКА ЧЕЛОВЕКА — ОДНОЙ РУКОЙ (владелец 2026-08-03, по образцу «Продуктов»). Её открывают и ряды веб-раздела,
 *  и ряды РОДНОЙ страницы «Контактов»: карточка со всеми полями, правкой и удалением остаётся веб-шторкой.
 *  НА ТЕЛЕФОНЕ В БРАУЗЕРЕ — ВБОК, ИЗ НЕЁ ПЕРЕПИСКА — СТОПКОЙ БЕЗ ССЫЛКИ НАЗАД (APP-CLAIMS-1020 ред. 11, Д9 / 10.11;
 *  слово владельца 21.10.2026: «контакт открывается горизонталкой, нажимаешь на сообщение — сообщения выезжают снизу,
 *  и там ссылки на контакт ещё раз нет»; страницы сервера: строка списка contacts.ts `present: 'push'`, кружок
 *  «message» → `thread?dialog=<id>&inside=1` deck). Широкий экран — как был: лист-стопка в панели-телефоне. */
function useOpenContactCard(reload: () => void): (c: ContactRow) => void {
  const t = useTranslations('cabinet.contacts');
  const tm = useTranslations('cabinet.messages');
  const panel = useSlidePanel();
  /** Переписка ПОВЕРХ карточки стопкой с «×» (адрес раздела не меняется): тело — та же лента DialogPanel, что у
   *  «Чатов», кружка профиля в шапке нет (карточка и так под ней — `inside`). Модуль переписки — лениво: статический
   *  импорт замкнул бы кольцо contacts → messages-dialog → messages → contacts. Строка переписки — из её же ленты
   *  (`awaitDialogRow`, тот же кэш, что у списка «Чатов»), недостающее — из карточки. */
  const openThreadOver = (id: string, c: ContactRow): void => {
    void import('./messages-dialog').then(async ({ DialogPanel, awaitDialogRow }) => {
      const got = await awaitDialogRow(id);
      const row: DialogListRow = {
        id,
        channel: c.channel === 'ig' ? 'ig' : 'tg',
        accountKey: '',
        name: contactTitle(c, ''),
        username: c.username ?? '',
        avatarUrl: c.avatarUrl ?? '',
        lastText: '',
        lastKind: '',
        lastDirection: 'in',
        lastAt: '',
        unread: 0,
        windowUntil: null,
        takenOver: false,
        ...(got ?? {}),
      };
      panel.open({
        title: dialogTitle(row) || tm('noName'),
        // Имя и логин — двумя строками в строке кружков (`dr-substack`), как у шапки переписки из «Чатов»; без него
        // лист с подзаголовком рисовал бы имя крупным заголовком в теле.
        ...(row.username ? { subtitle: `@${row.username}`, subStack: true } : {}),
        sheet: true,
        deck: true,
        lead: 'close',
        winKey: 'contact-thread',
        body: <DialogPanel dialog={row} onChanged={reload} />,
      });
    });
  };
  return (c: ContactRow) => {
    // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 К25, кадр iOS K13-contact-card): имя — героем в
    // теле, шапка без заголовка, карточка как родной экран.
    const phone = phoneWebNow();
    // КРУЖОК «ОТКРЫТЬ ПЕРЕПИСКУ» СПРАВА В ШАПКЕ (кадр iOS K13-contact-card, ChatInfoSheet
    // headCircle bubble.left): есть переписка — тап уводит в неё адресом, как родная
    // карточка «Контактов» (onOpenChat ниже); переписки нет — кружка нет. Адрес переписки
    // знает строка списка, а если переписка с карточкой не связана — ручка поиска по
    // человеку (ТЗ WEB-CAUSES, дополнение 356): ContactChatBtn спросит её сам.
    const chatBtn = phone ? (
      <ContactChatBtn
        contactId={c.id}
        dialogId={c.dialogId ?? ''}
        // Переписка — стопкой поверх карточки (10.11), не уход в «Чаты» адресом: вкладка не меняется.
        onOpen={(id) => openThreadOver(id, c)}
      />
    ) : null;
    panel.open({
      title: phone ? '' : contactTitle(c, t('noName')),
      ...(chatBtn ? { headerRight: chatBtn } : {}),
      // ТЕЛЕФОН — БОКОВОЙ СЛОЙ СО СКОБКОЙ «‹» (10.11; iOS push), широкий — лист-стопка (Н39, §11.0 «стопка»).
      ...(phone ? { side: true, lead: 'back' as const } : { sheet: true, deck: true }),
      // Адрес карточки (ТЗ WEB-LOOK Э2.4): /app/contacts/<id>; только в браузере.
      ...panelAddress(sectionUrl('contacts', c.id), sectionUrl('contacts', '')),
      // ОДНА КАРТОЧКА НА ВСЕ ШИРИНЫ (владелец 11.10.2026, TARIFF-PAGE-TZ §11 П8: веб-вариант с «ужасными
      // стилями» снят): панель-телефон 460 на широком экране получает те же экраны, что телефон (Закон 3).
      body: (
        <ContactCardIos
          contact={c}
          name={contactTitle(c, '')}
          username={c.username}
          avatarUrl={c.avatarUrl}
          channel={c.channel}
          onDeleted={() => {
            panel.close();
            reload();
          }}
          onSaved={reload}
        />
      ),
    });
  };
}

/** Сколько фото людей готовим для родного списка (см. пояснение у вызова). */
const NATIVE_FACES = 60;

/** Сколько людей родной список дотягивает фоном (SCALE-PLAN Е2): ровно столько же, сколько
 *  ручка отдавала одним куском до страниц, — поведение экрана прежнее, а первый кадр
 *  рисуется первой же страницей из 50. */
// Фоновая дотяжка родного списка (SCALE-PLAN 15.2 п.1): 150 человек (3 страницы), дальше —
// только по событию конца списка `accountMore`. Было 500 и патч на каждую страницу —
// десять пересборок списка подряд, тапы и ввод проваливались (журнал 16.09).
const NATIVE_MAX = 150;
/** Склейка букв родного поиска (15.2 п.7). */
const SEARCH_DEBOUNCE_MS = 150;

/** Что карточка знает про ЖИВУЮ ПЕРЕПИСКУ человека. Из «Чатов» она есть всегда, из
 *  «Контактов» — когда переписка у человека есть (её адрес приезжает вместе со списком). */
export interface ContactCardDialog {
  /** Адрес переписки — по нему уводит «Открыть переписку». */
  id: string;
  /** Готовое слово канала и значок сети возле него (ChanTG · ChanIG). */
  channelValue: string;
  channelImage: string;
  /** Готовая строка «сколько осталось на ответ»; пусто — строки нет. */
  windowValue: string;
  /** Кто отвечает — готовое слово; пусто — строки нет. */
  mineValue: string;
  /** Пояснение под карточкой (окно ответа Инстаграма); пусто — нет. */
  hint: string;
  /** Мы уже ВНУТРИ этой переписки (карточку открыли по «i») — кнопки перехода нет. */
  inside: boolean;
}

/** Герой карточки — общий для обоих мест: фото (или буква), имя и вторая строка. */
export interface ContactCardHero {
  title: string;
  /** Вторая строка героя ГОТОВОЙ строкой (полное имя и/или @логин). */
  meta: string;
  /** Фото человека картинкой моста (data:image/…;base64); пусто — буквенная плитка. */
  photo?: string;
  /** Адрес фото в полный размер: тап по аватарке открывает просмотр. */
  photoUrl?: string;
  /** Первая буква имени для плитки-заглушки. */
  initial: string;
}

/**
 * ЗАПИСАТЬ РУЧНЫЕ МЕТКИ ЧЕЛОВЕКА — ОДНА РУКА НА ВЕСЬ КАБИНЕТ (П7 обхода владельца
 * 11.09.2026): её зовут и родной лист меток приложения, и веб-карточка в браузере.
 * Ручка PATCH заменяет список меток целиком; служебные отметки (`marks`) не трогает
 * (БЭКЛОГ п.13).
 *
 * КАРТОЧКИ У ПЕРЕПИСКИ МОЖЕТ НЕ БЫТЬ ВОВСЕ: диалог заводится и без неё (бот написал
 * первым, человек только оставил комментарий). Тогда пишем ПО ПЕРЕПИСКЕ — карточку
 * заводит и связывает с диалогом сама ручка; прежде рука тут молча выходила, и метка
 * «не добавлялась» (скрин владельца 10:28).
 */
export async function putContactTags(input: {
  contact: ContactRow | null;
  dialogId?: string;
  tags: string[];
}): Promise<boolean> {
  const { contact, dialogId = '' } = input;
  // Ни человека, ни переписки — писать вправду некуда.
  if (!contact && !dialogId) return false;
  const tags = input.tags
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 50);
  try {
    const res = await fetch('/api/cabinet/contacts', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(contact ? { id: contact.id, tags } : { dialogId, tags }),
    });
    // Журнал клиента (DIAGNOSTICS-CANON): по жалобе «метка не встала» первый ход — строка
    // журнала, а не догадка. Самих меток в журнале нет — только исход и их число.
    journalLog('contact.tags', {
      ok: res.ok ? 1 : 0,
      code: res.status,
      n: tags.length,
      ...(contact ? {} : { byDialog: 1 }),
    });
    return res.ok;
  } catch {
    // Обрыв сети: врать про записанную метку нельзя — зовущий покажет ошибку.
    journalLog('contact.tags', { ok: 0, code: 0, n: tags.length });
    return false;
  }
}

/**
 * КАРТОЧКА ЧЕЛОВЕКА — ОДНА РУКА НА «ЧАТЫ» И «КОНТАКТЫ» (владелец 09.09.2026: «карточка
 * контакта = карточка собеседника из „Чатов“ — ОДНА на оба места»). Здесь собирается
 * словарь родного листа (chatInfoOpen/chatInfoPatch) и разбираются его намерения: состав
 * строк, слова, правка и удаление живут ТОЛЬКО тут — второго набора слов у приложения
 * нет. Рисует лист натив (ChatInfoSheet.swift), из «Контактов» его открывает тап по ряду,
 * из «Чатов» — кнопка «i» в шапке переписки.
 *
 * Пишет ТА ЖЕ ручка кабинета, что и веб-карточка (ContactCard выше): второй записи
 * контакта не заводится. Правка идёт НА МЕСТЕ — карточка меняет факты на поля, как в вебе.
 *
 * INVARIANT(scriptera): ключи строк 'window' · 'channel' · 'account' · 'mine' не
 * переименовывать — по ним переписка узнаёт свои намерения (chatInfoTap), и они уже
 * уехали в стор. Новое — только НОВЫМИ ключами. Причина: CLAUDE.md запрет №8.
 */
export function useContactCardNative(opts: {
  /** Человек карточки; null — контакта ещё нет (переписка без карточки). */
  contact: ContactRow | null;
  /**
   * ПЕРЕПИСКА, ИЗ КОТОРОЙ ОТКРЫТА КАРТОЧКА (П7 обхода владельца 11.09.2026). Карточки у
   * переписки может не быть вовсе (бот написал первым, человек только оставил
   * комментарий) — тогда метка записывается ПО ПЕРЕПИСКЕ: ручка кабинета заводит карточку
   * сама и связывает её с диалогом. Пусто — карточку открыли из «Контактов», человек уже
   * есть.
   */
  dialogId?: string;
  /** Правку сохранили — источник списка перечитать. */
  onSaved?: () => void;
  /** Человека удалили — карточку закрыть, список перечитать. */
  onDeleted?: () => void;
  /** «Открыть переписку» — увести в неё (из «Контактов»). */
  onOpenChat?: (dialogId: string) => void;
}): {
  /** Собрать словарь листа. Зовётся в рендере: своей памятки у него нет, отправку
   *  бережёт отпечаток (патч шлём только на настоящую разницу). */
  build: (input: {
    cardId: string;
    dark: boolean;
    hero: ContactCardHero;
    /** Имя аккаунта, которым идёт переписка с человеком; пусто — строки «Аккаунт» нет. */
    account?: string;
    dialog?: ContactCardDialog | null;
  }) => NativeChatInfoData;
  /** Тап по строке листа: разобрали — true. */
  tap: (key: string) => boolean;
  /** Правка поля родным листом: разобрали — true. */
  set: (key: string, value: string) => boolean;
  /** Открыли другого человека или лист ушёл — снять правку и подтверждение. */
  reset: () => void;
} {
  const t = useTranslations('cabinet.contacts');
  // Слова строк переписки — общие с «Чатами» (второго словаря у карточки нет).
  const tm = useTranslations('cabinet.messages');
  const tc = useTranslations('cabinet');
  // Слово красной плашки свайпа — ТО ЖЕ, что у папок («Удалить»): у приложения один
  // вид списка с удалением, своих слов карточка не сочиняет (владелец 15.09.2026).
  const tf = useTranslations('cabinet.contacts.folders');
  const locale = useLocale();
  // ЛИСТ МЕТОК «СТОПКОЙ ВЫШЕ» (владелец 10.09.2026, обход, п.12г): метки — ручные; на
  // карточке строка «Метки» открывает родной лист поверх карточки со списком меток
  // человека и добавлением (выбрать из уже бывших у людей клиента или написать новую).
  // Правки «Изменить» и подстраницы «Служебные данные» больше нет (п.12в, 12д).
  const [tagsOpen, setTagsOpen] = useState(false);
  const known = usePersistentData<{ ok: boolean; tags: { name: string; count: number }[] }>(
    'cabinet:contact-tags',
    '/api/cabinet/contact-tags',
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  // ЧТО ЧЕЛОВЕК ПРОШЁЛ И ПОЛУЧАЛ (владелец 15.09.2026): боты и сообщения — те же факты,
  // по которым его отбирают фильтры и папки, — стоят на карточке своими группами. Пустой
  // адрес — запроса нет (переписка без карточки).
  const history = useContactHistory(opts.contact?.id ?? '');
  const historyWords = useContactHistoryWords();
  /** Группы «Получал сообщения», развёрнутые строкой «Ещё N» (id черновика). */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const contact = opts.contact;
  // ПРОГНОЗ ЦЕПОЧЕК (М3-веб, владелец 16.09.2026): «что придёт дальше» и «снят с цепочек»
  // теперь и в приложении. Кэш — ТОТ ЖЕ, что у веб-блока (второго источника не заводим);
  // второй продукт выключен (CHAINS_HIDDEN) — не спрашиваем вовсе.
  const askChains = !CHAINS_HIDDEN && !!contact;
  const forecast = usePersistentData<ChainForecastResp>(
    askChains ? `cabinet:chain-forecast:${contact.id}` : '',
    askChains ? `/api/cabinet/chain-forecast?contact=${contact.id}` : '',
    { accept: (raw) => !!(raw as ChainForecastResp)?.ok },
  );
  // Слова условий — ОБЩАЯ рука со строкой «Кто получит» и веб-блоком прогноза.
  const audWords = useAudienceWords();
  /** Открытая страница одного будущего сообщения ('' — карточка как обычно). */
  const [msgOpen, setMsgOpen] = useState('');
  /** «Пропустить» отбито ручкой (409): сообщение уже ушло или отменено. */
  const [skipGone, setSkipGone] = useState(false);
  // Руки — РЕФАМИ: подписка на события листа живёт одна, а обработчик обязан видеть
  // свежего человека и свежую форму (иначе «Удалить» удаляло бы того, кто был открыт
  // первым, а «Сохранить» слало бы пустые поля).
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;
  /** Адрес переписки последнего собранного словаря — им уводит «Открыть переписку». */
  const dialogRef = useRef('');

  const fmtDate = (iso: string) =>
    iso
      ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
      : '—';

  const reset = () => {
    setTagsOpen(false);
    setConfirm(false);
    setErr(false);
    setBusy(false);
    setMsgOpen('');
    setSkipGone(false);
    setExpanded(new Set());
  };

  /** Записать метки человека — общей рукой раздела (putContactTags ниже). */
  const saveTags = (nextClient: string[]) => {
    const c = optsRef.current.contact;
    const dialogId = optsRef.current.dialogId ?? '';
    setBusy(true);
    setErr(false);
    void (async () => {
      const ok = await putContactTags({ contact: c, dialogId, tags: nextClient });
      if (ok) {
        optsRef.current.onSaved?.();
        known.reload();
      } else {
        setErr(true);
      }
      setBusy(false);
    })();
  };

  const del = () => {
    const c = optsRef.current.contact;
    if (!c) return;
    // Подтверждение — ДВУМЯ НАЖАТИЯМИ, как в вебе (подпись кнопки меняется), а не
    // системным окном: своего вопроса веб-карточка не показывает.
    if (!confirmRef.current) {
      setConfirm(true);
      return;
    }
    setBusy(true);
    void (async () => {
      try {
        const res = await fetch('/api/cabinet/contacts', {
          method: 'DELETE',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ id: c.id }),
        });
        if (res.ok) optsRef.current.onDeleted?.();
      } finally {
        setBusy(false);
      }
    })();
  };

  /** Слова прогноза цепочек — ОДИН набор на группы и на страницу сообщения (М3-веб). */
  const chainWords = {
    next: t('chainNext'),
    nextNone: t('chainNextNone'),
    row: (title: string, n: number) => `${title} · ${t('chainMsgN', { n })}`,
    when: (iso: string) =>
      new Date(iso).toLocaleString(locale, {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      }),
    after: (n: number, why: string) =>
      n > 0 && why !== '' ? t('chainAfterOut', { n, why }) : t('chainAfterOutPlain'),
    why: (list: readonly AudienceValue[]) =>
      list
        .map((f) => audWords(f))
        .filter((x) => x !== '')
        .join(' · '),
    waiting: (name: string) => (name ? t('chainWaitingFor', { name }) : t('chainWaitingForBot')),
    stopped: t('chainStopped'),
    reason: (key: string) => chainStopWords(key, t),
    restore: t('chainResume'),
    title: (n: number) => t('chainMsgN', { n }),
    chain: t('chainFldChain'),
    whenLbl: t('chainFldWhen'),
    whyLbl: t('chainFldWhy'),
    skip: t('chainSkipOne'),
    remove: t('chainRemoveOne'),
    none: t('chainNoCond'),
  };

  /** Перечитать прогноз после действия (пропустили, убрали, вернули). */
  const reloadChains = () => forecast.reload();

  /** Действие прогноза одной рукой: ручка та же, что у веб-блока (второй нет). */
  const chainAct = (body: Record<string, string>, onGone?: () => void) => {
    setBusy(true);
    void (async () => {
      const res = await fetch('/api/cabinet/chain-forecast', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => null);
      setBusy(false);
      if (res?.status === 409) {
        onGone?.();
        return;
      }
      if (res) reloadChains();
    })();
  };

  const build = (input: {
    cardId: string;
    dark: boolean;
    hero: ContactCardHero;
    account?: string;
    dialog?: ContactCardDialog | null;
  }): NativeChatInfoData => {
    const c = contact;
    const dialog = input.dialog ?? null;
    dialogRef.current = dialog?.id ?? '';
    const groups: NativeAccountGroup[] = [];
    // Ручные метки человека — и строкой в пузыре, и списком родного листа меток (п.12г).
    const marks = c ? c.tags : [];
    {
      // «ОСТАНОВИЛ БОТА» (ТЗ 15) — плашкой сверху, теми же словами, что в вебе.
      if (c?.stoppedAt) {
        groups.push({
          key: 'ct-stop',
          tone: 'warn',
          toneColor: readRoleHex('--amber', '#d99000'),
          hint: t('stopped', { date: fmtDate(c.stoppedAt) }),
          fields: [],
        });
      }
      // ── ПУЗЫРЬ 1: ТОЛЬКО ЧЕЛОВЕЧЕСКОЕ (владелец 09.09.2026, «Контакты» п.4: карточка
      // переделана по образцу прежней из «Чатов» — «привёл к общему, но взял худшее»).
      // Сколько осталось на ответ, канал, аккаунт, чем связаться и кто отвечает. Ни
      // идентификатора, ни служебных меток, ни переменных тут нет — они ушли на
      // подстраницу «Служебные данные» (ниже). @логин не дублируем: он стоит в герое.
      const facts: NativeAccountField[] = [];
      // ОКНО ОТВЕТА и КТО ОТВЕЧАЕТ — только там, где есть живая переписка (ТЗ 09.09.2026).
      if (dialog?.windowValue) {
        facts.push({
          key: 'window',
          kind: 'value',
          title: tm('infoWindow'),
          value: dialog.windowValue,
        });
      }
      // Канал: слово и значок сети — из переписки, а нет её — из карточки человека.
      if (dialog?.channelValue) {
        facts.push({
          key: 'channel',
          kind: 'value',
          title: tm('infoChannel'),
          valueImage: dialog.channelImage,
          value: dialog.channelValue,
        });
      } else if (c) {
        facts.push({
          key: 'channel',
          kind: 'value',
          title: t('fldChannel'),
          value: channelLabel(c.channel, locale),
        });
      }
      if (input.account) {
        facts.push({
          key: 'account',
          kind: 'value',
          title: tm('infoAccount'),
          value: input.account,
        });
      }
      if (c?.phone)
        facts.push({ key: 'ct:v:phone', kind: 'value', title: t('fldPhone'), value: c.phone });
      if (c?.email)
        facts.push({ key: 'ct:v:email', kind: 'value', title: t('fldEmail'), value: c.email });
      // Замок диалога — строка-действие: тап уходит вебу (chatInfoTap), а спрашивает и
      // переключает он сам — теми же словами и тем же вопросом, что на веб-карточке.
      // СЛОВА КОРОТКИЕ (ТЗ п.4): «Отвечает — Бот» / «Отвечает — Вы», а не «Кто отвечает —
      // Выключить автоматизации» (длинное значение не помещалось и обрезалось).
      if (dialog?.mineValue) {
        facts.push({ key: 'mine', kind: 'nav', title: tm('infoAnswers'), value: dialog.mineValue });
      }
      groups.push({
        key: 'ct-facts',
        fields: facts,
        // Сноска про 24 часа Инстаграма ПРИНАДЛЕЖИТ этому пузырю (правило волны 2 П2):
        // стоит под ним, а не в самом низу карточки, где читалась как общий подвал.
        ...(dialog?.hint ? { hint: dialog.hint } : {}),
      });
      // ── ПУЗЫРЬ 2: «Активность» — когда виделись и с каких пор человек у нас.
      if (c) {
        groups.push({
          key: 'ct-when',
          label: t('activity'),
          fields: [
            // «Последний раз» — последнее взаимодействие с ботом, а не «был онлайн»
            // (владелец 10.09.2026: «Был(а)» — неправда).
            { key: 'ct:v:seen', kind: 'value', title: t('fldLast'), value: fmtDate(c.lastSeenAt) },
            { key: 'ct:v:since', kind: 'value', title: t('fldSince'), value: fmtDate(c.createdAt) },
          ],
        });
      }
      // ── ПУЗЫРЬ 3: МЕТКИ — КАЖДАЯ СВОЕЙ СТРОКОЙ (владелец 15.09.2026: «это должен быть
      // список, где каждая метка — новая строчка»). ВИД — РОВНО КАК У ПАПОК (владелец
      // 15.09.2026: «там был один дизайн, тут он с какого-то другой»): «Добавить метку» —
      // акцентной кнопкой ПЕРВОЙ строкой пузыря, как «Создать папку»; свайп строки влево —
      // красная плашка «Удалить» тем же словом, что у папок; кнопка открывает родной
      // список всех меток клиента с галочками (стопка поверх карточки, п.12г).
      // ГРУППА ЕСТЬ И БЕЗ КАРТОЧКИ (П7 обхода владельца 11.09.2026): метку ставят прямо
      // из переписки, а карточку человека ручка кабинета заводит сама при первой метке.
      if (c || opts.dialogId) {
        groups.push({
          key: 'ct-tags',
          label: t('fldTags'),
          fields: [
            // Кнопка открывает НАСТРОЙКУ меток (список с галочками), а не только добавление
            // (владелец 15.09.2026: «не „добавить метку“, а „настроить метки“»).
            { key: 'ct:tags', kind: 'button', title: t('tagsSetup'), tone: 'accent' },
            ...marks.map((x): NativeAccountField => ({
              key: `ct:tagrow:${x}`,
              kind: 'value',
              title: contactTagLabel(x, locale),
              swipeDelete: `ct:tag:rm:${x}`,
              swipeLabel: tf('deleteShort'),
            })),
          ],
        });
      }
      // ── ПУЗЫРИ 4 и 5: ПРОШЁЛ БОТОВ и ПОЛУЧАЛ СООБЩЕНИЯ (владелец 15.09.2026) — то, по
      // чему человека отбирают фильтры, видно на его карточке. Только чтение: это журнал,
      // а не настройка. Пока едет — пустой пузырь той же высоты; приехало пусто — слово.
      if (c) {
        // П195: короткие даты, свежие сверху, «Получал сообщения» — группой на бота с
        // «Ещё N». Сборщик ОДИН с веб-карточкой (lib/contact-card-rows.ts).
        // ПОКА ИСТОРИИ НЕТ — ГРУПП НЕТ (правило 1 владельца, PREFETCH-TZ §2.3): пустые пузыри «Прошёл ботов» /
        // «Получал сообщения» не показываем. Карточку греет список (warmContactCard) — обычно история уже в кэше.
        if (history.data)
          groups.push(...contactHistoryGroups(history.data, historyWords, expanded));
      }
      // ── ПУЗЫРИ 6 и 7: ЧТО ПРИДЁТ ДАЛЬШЕ и СНЯТ С ЦЕПОЧЕК (владелец 16.09.2026,
      // М3-веб). Тот же состав, что у веб-блока `ContactChainNext`: будущие сообщения с
      // датами, где человек выйдет, и возврат в цепочку по одной.
      if (c && askChains) groups.push(...chainCardGroups(forecast.data ?? null, chainWords));
      if (err) groups.push({ key: 'ct-err', tone: 'danger', hint: t('saveError'), fields: [] });
      // Внизу — только «Удалить контакт» (владелец 10.09.2026, п.12е); «Открыть переписку»
      // ушла кружком в шапку листа (head.open), «Изменить» снята вовсе.
      if (c) {
        groups.push({
          key: 'ct-act',
          fields: [
            {
              key: 'ct:del',
              kind: 'button',
              title: confirm ? t('deleteSure') : t('delete'),
              tone: 'danger',
              disabled: busy,
            },
          ],
        });
      }
    }
    // СТРАНИЦА ОДНОГО БУДУЩЕГО СООБЩЕНИЯ (М3-веб) — ТОЙ ЖЕ подстраницей карточки, что
    // когда-то показывала «Служебные данные»: натив рисует её ВМЕСТО карточки, шагом
    // вглубь того же листа, «назад» возвращает событием ct:tech:back. Своего экрана под
    // это не заводим и ключей модели не добавляем.
    const msgPage = msgOpen ? chainCardPage(forecast.data ?? null, msgOpen, chainWords) : null;
    return {
      dialogId: input.cardId,
      theme: input.dark ? 'dark' : 'light',
      hero: {
        color: readRoleHex('--accent-stroke', '#e23570'),
        title: input.hero.title,
        meta: input.hero.meta,
        photo: input.hero.photo ?? '',
        // Адрес фото в полный размер (владелец 09.09.2026): тап по аватарке в родной
        // карточке открывает его тем же просмотром, что кадр публикации.
        photoUrl: input.hero.photoUrl ?? '',
        initial: input.hero.initial,
      },
      // ПЛОСКИЙ СПИСОК — старым сборкам (они читают только его): там же, что и в первом
      // пузыре свежих. Своих слов для них не сочиняем.
      fields: groups.find((g) => g.key === 'ct-facts')?.fields ?? [],
      // Сноска про окно ответа теперь стоит под СВОИМ пузырём (см. группу ct-facts) —
      // общего подвала у карточки больше нет.
      hint: '',
      groups,
      // КРУЖКИ ШАПКИ ЛИСТА (п.12е): слева крестик, справа «Открыть переписку» — только
      // когда переписка есть и мы не внутри неё. Старые сборки поле не знают.
      head: {
        close: tc('close'),
        ...(dialog && dialog.id && !dialog.inside
          ? { open: { key: 'ct:open', label: t('openChat') } }
          : {}),
      },
      ...(msgPage
        ? {
            sub: {
              title: msgPage.title,
              groups: skipGone
                ? [
                    ...msgPage.groups,
                    {
                      key: 'ct-msg-gone',
                      tone: 'hint' as const,
                      hint: t('chainSkipGone'),
                      fields: [],
                    },
                  ]
                : msgPage.groups,
            },
          }
        : {}),
      // ЛИСТ МЕТОК (п.12г): открыт/закрыт решает веб; список известных — все ручные метки
      // людей клиента (служебные отрезаны ручкой); слова — отсюда.
      tags: {
        open: tagsOpen,
        title: t('fldTags'),
        items: marks,
        known: (known.data?.tags ?? []).map((x) => x.name).filter((x) => !marks.includes(x)),
        // ВСЕ МЕТКИ КЛИЕНТА ОДНИМ СПИСКОМ (П17 обхода владельца 11.09.2026, круг 2):
        // родному листу нужны и те, что уже стоят у человека, — он рисует общий список с
        // галочками. Порядок — как у ручки (по частоте), метки самого человека, которых
        // ручка ещё не знает (только что поставили), дописываются в конец.
        all: [
          ...(known.data?.tags ?? []).map((x) => x.name),
          ...marks.filter((x) => !(known.data?.tags ?? []).some((k) => k.name === x)),
        ],
        allLabel: t('tagsAll'),
        addLabel: t('tagsAdd'),
        // КОРОТКОЕ СЛОВО КНОПКИ У ПОЛЯ (П7, договор с родным листом 11.09.2026): в капсуле
        // рядом с полем длинное «Добавить метку» не помещается. Ключ ДОБАВОЧНЫЙ — старая
        // сборка его не знает и берёт `addLabel`, как раньше (запрет №8).
        addBtn: t('tagsAddBtn'),
        addPh: t('tagsAddPh'),
        emptyHint: t('tagsEmpty'),
        doneLabel: tc('done'),
        busy,
        // СВАЙП «УДАЛИТЬ» В РОДНОМ СПИСКЕ МЕТОК (владелец 15.09.2026): слово плашки — то
        // же, что у папок; вопрос подтверждения и «Отмена» — слова кабинета. Ключи
        // добавочные (запрет №8): старая сборка их не знает — свайпа у неё просто нет.
        delLabel: tf('deleteShort'),
        delAsk: t('tagDelAsk'),
        cancel: t('cancel'),
        sharedNote: t('tagsSharedNote'),
      },
    };
  };

  const tap = (key: string): boolean => {
    // ── ПРОГНОЗ ЦЕПОЧЕК (М3-веб) ────────────────────────────────────────────────────
    if (key === CT_CHAIN_BACK || key === 'ct:tech:back') {
      setMsgOpen('');
      setSkipGone(false);
      return true;
    }
    if (key.startsWith(CT_CHAIN_MSG)) {
      setSkipGone(false);
      setMsgOpen(key.slice(CT_CHAIN_MSG.length));
      return true;
    }
    if (key.startsWith(CT_CHAIN_SKIP)) {
      // Пропустить ОДНО сообщение: следующие идут дальше по своим срокам. Ручка отбила
      // 409 — сообщение уже ушло или отменено, говорим об этом строкой и не врём.
      chainAct({ cancel: key.slice(CT_CHAIN_SKIP.length) }, () => setSkipGone(true));
      return true;
    }
    if (key.startsWith(CT_CHAIN_STOP)) {
      // Убрать из цепочки целиком — дальше она встанет в «Снят с цепочек» с «Вернуть».
      chainAct({ stop: key.slice(CT_CHAIN_STOP.length) });
      setMsgOpen('');
      return true;
    }
    if (key.startsWith(CT_CHAIN_BACKIN)) {
      chainAct({ memberId: key.slice(CT_CHAIN_BACKIN.length) });
      return true;
    }
    // «Ещё N» в группе «Получал сообщения» (П195): развернуть группу бота.
    if (key.startsWith(CT_MORE)) {
      const id = key.slice(CT_MORE.length);
      setExpanded((prev) => new Set([...prev, id]));
      return true;
    }
    if (key === 'ct:tags') {
      setTagsOpen(true);
      return true;
    }
    if (key === 'ct:tags:close') {
      setTagsOpen(false);
      return true;
    }
    if (key.startsWith('ct:tag:add:')) {
      const name = key.slice('ct:tag:add:'.length).trim();
      // КАРТОЧКИ ЕЩЁ НЕТ — СПИСОК ПУСТ, А МЕТКА ВСЁ РАВНО СТАВИТСЯ (П7 обхода владельца
      // 11.09.2026): карточку заведёт ручка кабинета по переписке.
      const have = optsRef.current.contact?.tags ?? [];
      // Служебную отметку в метки не пускаем — замок на случай опечатки; сам детектор
      // ОДИН на продукт (@scriptera/db).
      if (name && !isServiceMark(name)) saveTags([...have.filter((x) => x !== name), name]);
      return true;
    }
    if (key.startsWith('ct:tag:rm:')) {
      const name = key.slice('ct:tag:rm:'.length);
      const c = optsRef.current.contact;
      if (c) saveTags(c.tags.filter((x) => x !== name));
      return true;
    }
    // УДАЛИТЬ МЕТКУ У ВСЕХ (свайп в родном списке, владелец 15.09.2026) — той же ручкой,
    // что «Данные людей» в настройках: метка снимается со всех карточек клиента.
    if (key.startsWith('ct:tag:del:')) {
      const name = key.slice('ct:tag:del:'.length).trim();
      if (!name) return true;
      setBusy(true);
      void (async () => {
        try {
          const res = await fetch('/api/cabinet/contact-tags', {
            method: 'DELETE',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ name }),
          });
          journalLog('contact.tags', { ok: res.ok ? 1 : 0, code: res.status, n: 0, del: 1 });
          if (res.ok) {
            optsRef.current.onSaved?.();
            known.reload();
            refreshTagCaches();
          } else {
            setErr(true);
          }
        } finally {
          setBusy(false);
        }
      })();
      return true;
    }
    if (key === 'ct:del') {
      del();
      return true;
    }
    if (key === 'ct:open') {
      const id = dialogRef.current;
      if (id) optsRef.current.onOpenChat?.(id);
      return true;
    }
    return false;
  };

  // Полей правки у карточки больше нет (п.12в): набранное в листе меток приходит тапом
  // `ct:tag:add:<слово>`, а не полем.
  const set = (): boolean => false;

  return { build, tap, set, reset };
}

/**
 * ВОДИТЕЛЬ РОДНОЙ КАРТОЧКИ ЧЕЛОВЕКА — ОДИН НА ВЕСЬ КАБИНЕТ (владелец 14.09.2026: «нужно
 * было открыть СУЩЕСТВУЮЩУЮ шторку, ты сделал новую»). Кто бы ни показывал человека —
 * «Контакты», переписка или журнал окна бота — лист открывает ЭТА рука: открыть один раз,
 * дальше патчи на настоящую разницу, намерения — событиями листа. Второй копии водителя
 * в кабинете нет.
 *
 * Открыт ровно тогда, когда пришёл `contact`; сменился человек — лист переезжает на него.
 */
export function useContactCardHost(opts: {
  /** Кого показываем; null — лист закрыт. */
  contact: ContactRow | null;
  /** Имя аккаунта переписки; пусто — строки «Аккаунт» в листе нет. */
  accountName?: string;
  /** Готовое лицо со склада кабинета; пусто — буквенная плитка. */
  photo?: string;
  /** Правку сохранили. */
  onSaved?: () => void;
  /** Человека удалили (лист уже закрыт). */
  onDeleted?: () => void;
  /** Лист закрыли жестом. */
  onGone?: () => void;
  /** «Открыть переписку» (лист уже закрыт) — увести в неё. */
  onOpenChat?: (dialogId: string) => void;
}): { open: boolean } {
  const t = useTranslations('cabinet.contacts');
  const dark = useIsDark();
  const contact = opts.contact;
  const id = contact?.id ?? '';
  const handRef = useRef(opts);
  handRef.current = opts;
  const card = useContactCardNative({
    contact,
    onSaved: () => handRef.current.onSaved?.(),
    onDeleted: () => {
      void nativeBridge()?.chatInfoClose?.();
      handRef.current.onDeleted?.();
    },
    onOpenChat: (dialogId) => {
      void nativeBridge()?.chatInfoClose?.();
      handRef.current.onOpenChat?.(dialogId);
    },
  });
  const data = contact
    ? card.build({
        cardId: `ct:${contact.id}`,
        dark,
        account: opts.accountName ?? '',
        hero: {
          title: contactTitle(contact, t('noName')),
          // ИМЯ РАВНО ЛОГИНУ — ВТОРОЙ СТРОКИ НЕТ (ТЗ «Контакты» п.3): «@ivanov» под
          // «@ivanov» — дубль, а не сведения. Имя укоротили — полное встаёт второй
          // строкой, через точку с логином (та же форма, что у карточки из «Чатов», §3.5).
          meta: [
            fullNameNote(contact.name, contactTitle(contact, t('noName'))),
            contact.username &&
            contactTitle(contact, t('noName')) !== `@${contact.username.replace(/^@/, '')}`
              ? `@${contact.username.replace(/^@/, '')}`
              : '',
          ]
            .filter(Boolean)
            .join(' · '),
          photo: opts.photo ?? '',
          initial: contactInitial(contactTitle(contact, t('noName'))),
        },
        // Переписка у человека может быть, а может и не быть: её адрес приезжает со
        // списком. Слов переписки («сколько осталось на ответ», «кто отвечает») отсюда
        // не считаем — их знает только сама переписка.
        dialog: contact.dialogId
          ? {
              id: contact.dialogId,
              channelValue: '',
              channelImage: '',
              windowValue: '',
              mineValue: '',
              hint: '',
              inside: false,
            }
          : null,
      })
    : null;
  /** Что уже уехало нативу: патч шлём только на настоящую разницу. */
  const sentRef = useRef('');
  const dataRef = useRef(data);
  dataRef.current = data;
  const resetRef = useRef(card.reset);
  resetRef.current = card.reset;
  useEffect(() => {
    if (!id) return;
    const br = nativeBridge();
    const first = dataRef.current;
    if (!br?.chatInfoOpen || !first) return;
    void br.chatInfoOpen(first);
    sentRef.current = JSON.stringify(first);
    return () => {
      sentRef.current = '';
      resetRef.current();
      void nativeBridge()?.chatInfoClose?.();
    };
    // Открываем ОДИН раз на человека: правка, метки и тема едут патчем (первый словарь
    // берём рефом — от него зависеть нельзя, он пересобирается каждый рендер).
  }, [id]);
  useEffect(() => {
    if (!data || !sentRef.current) return;
    const next = JSON.stringify(data);
    if (next === sentRef.current) return;
    sentRef.current = next;
    void nativeBridge()?.chatInfoPatch?.(data);
  });
  // Намерения листа. Рука — рефом: подписка живёт одна, а значения обязаны быть свежими.
  const tapRef = useRef(card.tap);
  tapRef.current = card.tap;
  const setRef = useRef(card.set);
  setRef.current = card.set;
  const idRef = useRef(id);
  idRef.current = id;
  useEffect(() => {
    if (!nativeMethod('chatInfoOpen')) return;
    const br = nativeBridge();
    if (!br?.addListener) return;
    let dead = false;
    const subs: Array<{ remove: () => void | Promise<void> }> = [];
    void (async () => {
      const add = async (
        ev: 'chatInfoTap' | 'chatInfoGone' | 'chatInfoSet',
        cb: (d: { key?: string; value?: string | boolean | string[]; dialogId?: string }) => void,
      ): Promise<void> => {
        const sub = await br.addListener!(ev, cb);
        if (dead) void sub.remove();
        else subs.push(sub);
      };
      // Карточка человека и карточка собеседника — один лист на двоих: чужие события
      // (переписка открыта под нами) отсекаем по ключу карточки.
      const mine = (d: { dialogId?: string }) =>
        !!idRef.current && d?.dialogId === `ct:${idRef.current}`;
      await add('chatInfoTap', (d) => {
        if (mine(d)) tapRef.current(d?.key ?? '');
      });
      await add('chatInfoSet', (d) => {
        if (mine(d) && typeof d?.value === 'string') setRef.current(d?.key ?? '', d.value);
      });
      await add('chatInfoGone', (d) => {
        if (!mine(d)) return;
        resetRef.current();
        handRef.current.onGone?.();
      });
    })();
    return () => {
      dead = true;
      for (const sub of subs) void sub.remove();
    };
  }, []);

  return { open: !!id };
}

/**
 * КАРТОЧКА ЧЕЛОВЕКА ИЗ «КОНТАКТОВ» — ТЕМ ЖЕ РОДНЫМ ЛИСТОМ, ЧТО В «ЧАТАХ» (владелец
 * 09.09.2026). Прежняя родная СТРАНИЦА карточки (стопка со своим героем) отменена: тап по
 * ряду открывает лист собеседника, а показывает его общий водитель выше.
 */
function useNativeContactCard(
  contacts: ContactRow[],
  accounts: ContactAccount[],
  faces: Record<string, string>,
  reload: () => void,
): { openCard: (id: string) => void; open: boolean } {
  const router = useRouter();
  const [id, setId] = useState('');
  const contact = contacts.find((c) => c.id === id) ?? null;
  const host = useContactCardHost({
    contact,
    accountName: accounts.find((a) => a.key === contact?.botId)?.name ?? '',
    photo: contact?.avatarUrl ? faces[contact.avatarUrl] : '',
    onSaved: reload,
    onDeleted: () => {
      setId('');
      reload();
    },
    onGone: () => setId(''),
    onOpenChat: (dialogId) => {
      setId('');
      // ПЕРЕПИСКА — ПРОСЬБОЙ, А НЕ АДРЕСОМ (владелец 11.09.2026, П10г: тап по контакту
      // открывал «Чаты» без переписки; журнал 11.09, 10:32:44 → 10:32:46 — вкладка
      // сменилась, `chatOpen` не пришёл вовсе). В приложении просим раздел «Чаты» открыть
      // её сам (lib/open-dialog.ts), а адресу остаётся привести человека на вкладку;
      // в браузере путь прежний — адрес с id переписки.
      // СНАЧАЛА ВКЛАДКА, ПОТОМ ПРОСЬБА (П19 обхода владельца 11.09.2026, круг 2): раздел
      // «Чаты» исполняет её только на своей активной вкладке, и порядок тут важен.
      const native = isNativeApp();
      router.push(native ? '/app/messages' : `/app/messages/${dialogId}`);
      const asked = native ? openDialogNative(dialogId) : false;
      journalLog('ct.openChat', { ask: asked ? 1 : 0 });
    },
  });
  return { openCard: (next: string) => setId(next), open: host.open };
}

/**
 * РОДНАЯ СТРАНИЦА «КОНТАКТЫ» (docs/NATIVE-TABS-PLAN.md; переделка по ТЗ 09.09.2026).
 *
 * ЭТАЛОН — РОДНЫЕ СПИСКИ «БОТЫ» И «ЧАТЫ», а не веб-таблица (владелец 09.09.2026):
 *   • ГЕРОЯ НЕТ: заголовок «Контакты» стоит в шапке страницы;
 *   • ПОИСК — та же плашка-вход (`ScrSearchPlate`) и тот же родной поиск сверху
 *     (`openSearch` → searchQuery), что у «Ботов»: своего поля у страницы нет;
 *   • РЯД — как ряд «Чатов»: фото или буквенная плитка, имя одной строкой, дата справа,
 *     вторая строка @логин, чипы меток только когда метки есть;
 *   • кнопка-воронка отбора в шапке: метки, канал, аккаунт (с подписью сети) — те же
 *     секции и те же условия показа, что у веб-листа;
 *   • «Показаны свежие N из M» над списком, пустые состояния empty / filterEmpty;
 *   • ИСКУССТВЕННОГО ПОТОЛКА НЕТ: приезжают все, кого отдал сервер.
 * Карточка человека — ТОТ ЖЕ РОДНОЙ ЛИСТ, ЧТО В «ЧАТАХ» (`useContactCardNative` выше):
 * страницы стопки под неё больше нет.
 *
 * `stack` — что открыто прямо сейчас: пока свод «Настроек» не открыт, страница в сеть
 * не ходит (кэш кабинета показывает, запросов не делает).
 */
export function useNativeContactsPage(
  hero: NativeAccountHero,
  stack: string[],
): {
  pages: NativeAccountPage[];
  tapRow: (rowKey: string) => boolean;
  applySet: (rowKey: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.contacts');
  // Подписи отбора по каналу и аккаунту — те же слова, что у «Сообщений» (общий словарь).
  const tm = useTranslations('cabinet.messages');
  const locale = useLocale();
  const open = stack.includes('settings');
  /** Человек на вкладке «Аккаунт» (вместе с «Настройками» — это одна вкладка меню):
   *  отсюда до «Контактов» два тапа, и это наша фора на прогрев списка (П28). */
  const pathname = usePathname();
  const onAccount = /\/app\/(account|settings)(?:\/|$)/.test(pathname ?? '');
  /** Страница «Контактов» открыта прямо сейчас: пока нет — родной поиск не наш. */
  const here = stack.includes('contacts');
  const [q, setQ] = useState('');
  const qTimer = useRef<number | null>(null);
  // ПАПКИ И ФИЛЬТР (владелец 10.09.2026): отбор считает СЕРВЕР — ?folder=<активная папка>
  // и ?f=<условия воронки> (те же условия, что у аудитории рассылки, плюс «Аккаунт»).
  // На клиенте остаётся только поиск по слову.
  const [val, setVal] = useState<AudienceValue>({});
  const folders = useContactFolders();
  const fltOn = audienceCount(val);
  /**
   * АДРЕС СПИСКА ЛЮБОЙ ПАПКИ при текущем отборе — ОДНА рука на свой список и на соседние
   * страницы (П10б). Своей строки отбора у второго места заводить нельзя: другой порядок
   * частей дал бы ВТОРОЙ кэш того же списка.
   */
  const listUrlOf = useCallback(
    (folderId: string): string => {
      const qs = new URLSearchParams();
      if (folderId) qs.set('folder', folderId);
      if (fltOn > 0) qs.set('f', JSON.stringify(val));
      const tail = qs.toString();
      return tail ? `${CONTACTS_URL}?${tail}` : CONTACTS_URL;
    },
    [fltOn, val],
  );
  const listUrl = listUrlOf(folders.current);
  // СТРАНИЦАМИ (SCALE-PLAN Е2). Первые порции родному списку доезжают ФОНОМ до NATIVE_MAX
  // (500 — столько он получал и до Е2), дальше — ПО ПРОСЬБЕ: свежие сборки приложения
  // шлют событие `accountMore {page}`, когда список подошёл к концу (это событие, не метод
  // моста — запрет №8 не задет), и веб досылает следующую страницу. Старые сборки события
  // не шлют — у них остаётся фоновая порция.
  const { data, contacts, reload, loadMore, busy, hasMore } = useContactsPages(
    listUrl,
    open,
    NATIVE_MAX,
  );
  // ОДИН ПАТЧ НА ДОТЯЖКУ (15.2 п.1): нативу уезжает не каждая страница, а «осевший»
  // список — первая страница сразу, дальше только когда фоновая дотяжка кончилась
  // (набрали NATIVE_MAX или страниц больше нет) и после каждой догрузки по accountMore.
  const [settledFor, setSettledFor] = useState<{ url: string; list: ContactRow[] }>({
    url: listUrl,
    list: contacts,
  });
  // ПАПКА СМЕНИЛАСЬ — РЯДЫ ТОЛЬКО ЕЁ (журнал 10:13:20 и видео, сборка 262, владелец
  // 16.09.2026: «дёргается»). Осевший список помнит, чьей папке принадлежит: пока он от
  // прежнего адреса, нативу уходят живые ряды новой папки (первая страница из кэша), а не
  // чужие 150 рядов на один кадр с последующей подменой.
  const settled = settledFor.url === listUrl ? settledFor.list : contacts;
  useEffect(() => {
    if (busy) return;
    const first = contacts.length <= CONTACTS_PAGE;
    const done = !hasMore || contacts.length >= NATIVE_MAX;
    if (first || done) {
      setSettledFor((prev) =>
        prev.url === listUrl && prev.list === contacts ? prev : { url: listUrl, list: contacts },
      );
    }
  }, [busy, hasMore, contacts, listUrl]);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    if (!open) return;
    const br = nativeBridge();
    if (!br?.addListener) return;
    let dead = false;
    let sub: { remove: () => void | Promise<void> } | null = null;
    void (async () => {
      const h = await br.addListener!('accountMore', (d) => {
        if (d.page === 'contacts') loadMoreRef.current();
      });
      if (dead) void h.remove();
      else sub = h;
    })();
    return () => {
      dead = true;
      if (sub) void sub.remove();
    };
  }, [open]);
  /** Порядок списка людей — ОДИН на свою папку и на соседние: живые сверху. */
  const byLastSeen = (list: readonly ContactRow[]): ContactRow[] =>
    [...list].sort((a, b) => (b.lastSeenAt || '').localeCompare(a.lastSeenAt || ''));
  // ПОИСК: сразу — своими силами по загруженному, следом приезжают находки сервера по ВСЕЙ
  // базе (приём «Чатов»). Своей меркой проходим и по ним — на экране мерка одна.
  const found = useContactsSearch(listUrl, q.trim());
  const shown = useMemo(
    () => byLastSeen(filterContacts(found?.q === q.trim() ? found.contacts : settled, q, 'all')),
    [settled, found, q],
  );

  /**
   * СОСЕДНИЕ ПАПКИ ДЛЯ СВАЙПА (владелец 11.09.2026, П10б; у «Чатов» это правила 6–7 от
   * 11.09). Листаются все папки полосы, кроме текущей; во время ПОИСКА страниц нет вовсе —
   * на экране находки, а не папка.
   */
  const folderIds = folders.folders.map((f) => f.id).join(',');
  const current = folders.current;
  const pageFolders = useMemo(
    () => (q ? [] : folderIds.split(',').filter((id) => id && id !== current)),
    [q, folderIds, current],
  );
  const pageUrls = useMemo(() => pageFolders.map(listUrlOf), [pageFolders, listUrlOf]);
  /**
   * ТИХИЙ ПРОГРЕВ СОСЕДНЕЙ ПАПКИ — тем же приёмом, что у «Чатов» (messages.tsx): ОДНА соседняя
   * (следующая по полосе, у последней — предыдущая; ТЗ ~/projects/SPEED-TZ.md §5.3), целью плана
   * прогрева (очередь держит фору и скрытую вкладку), повтор — только когда сменился набор папок,
   * текущая папка или отбор. Опрос сервера по-прежнему ведёт ТОЛЬКО текущая папка.
   */
  const warmedFolders = useRef<Set<string>>(new Set());
  const listReady = !!data;
  const nearUrl = useMemo(() => {
    if (q) return '';
    const all = folderIds.split(',').filter(Boolean);
    const at = all.indexOf(current);
    const near = at < 0 ? all.find((id) => id !== current) : (all[at + 1] ?? all[at - 1]);
    return near && near !== current ? listUrlOf(near) : '';
  }, [q, folderIds, current, listUrlOf]);
  useEffect(() => {
    if (!open || !listReady || !nearUrl) return;
    // Цель плана `contacts.folders` (У2): фору своему списку, скрытую вкладку и шаг держит очередь (lib/warm.ts).
    if (warmedFolders.current.has(nearUrl)) return;
    warmedFolders.current.add(nearUrl);
    warmTarget('contacts.folders', listKeyOf(nearUrl), nearUrl, { accept: acceptList });
  }, [open, listReady, nearUrl]);
  /**
   * «КОНТАКТЫ» ОТКРЫВАЮТСЯ БЕЗ ЗАДЕРЖКИ (П28 обхода владельца 11.09.2026, круг 3).
   * Страница ходит в сеть только из открытого свода «Настроек» (`open` выше) — до первого
   * тапа кэш пуст, и человек видит пустой список с загрузкой. Поэтому, как только веб
   * знает, что человек на вкладке «Аккаунт», список греется в кэш тем же приёмом, что
   * подстраницы настроек (warm-cache.tsx): первая страница СВОЕЙ папки, следом соседние —
   * по одной с шагом, чтобы не выстреливать залпом на медленной сети.
   *
   * ФОРА своему экрану: сначала «Аккаунт» дорисовывается и его запросы уходят, и только
   * потом лезем мы. Греем один раз на набор адресов, не на фоновой вкладке и только в
   * приложении — в браузере родной страницы «Контактов» нет вовсе, и `open` там никогда
   * не становится правдой.
   *
   * Папки греть не нужно: их ручку `useContactFolders` зовёт БЕЗУСЛОВНО (адрес всегда
   * задан), и к этому месту они уже едут — второй запрос за тем же ключом ничего не
   * ускорит.
   */
  const warmedList = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (open || !onAccount || !isNativeApp() || !folders.ready || !listUrl) return;
    const urls = [listUrl, ...pageUrls];
    const base = urls.join('\n');
    // Цель плана `contacts.list-ahead` (У3): фору «Аккаунту», шаг и скрытую вкладку держит очередь (lib/warm.ts).
    if (warmedList.current.has(base)) return;
    warmedList.current.add(base);
    journalLog('ct.warm', { n: urls.length, folder: folders.current.slice(0, 8) });
    for (const url of urls) warmTarget('contacts.list-ahead', listKeyOf(url), url, { accept: acceptList });
    // listKeyOf — чистая рука от тех же `fltOn`/`val`, что уже стоят в `listUrl`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onAccount, folders.ready, folders.current, listUrl, pageUrls]);
  const pageCache = usePersistentPeek<ContactsResp>(pageUrls.map(listKeyOf));
  /** Люди соседних папок из КЭША (второго запроса и второго кэша нет). */
  // ДОГРУЖЕННОЕ НЕ ПРОПАДАЕТ ПРИ СМЕНЕ ПАПКИ (владелец 16.09.2026, сборка 264, тот же
  // приём, что у «Чатов»): папка, из которой ушли, помнит свой осевший список; соседняя
  // страница получает его, а не первую страницу из кэша, если он длиннее.
  const loadedByUrl = useRef<Map<string, ContactRow[]>>(new Map());
  if (settledFor.url === listUrl && settled.length > 0) loadedByUrl.current.set(listUrl, settled);
  const pageLists = useMemo(
    () =>
      pageFolders.map((id, i) => {
        const cached = pageCache[i]?.contacts ?? [];
        const known = loadedByUrl.current.get(listUrlOf(id));
        return {
          id,
          resp: pageCache[i] ?? null,
          list: byLastSeen(known && known.length > cached.length ? known : cached),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadedByUrl — ref, listUrlOf стабилен
    [pageFolders, pageCache, listUrlOf],
  );
  // ФОТО ГОТОВИМ ДЛЯ ВЕРХА СПИСКА. Строк у списка теперь СКОЛЬКО ЕСТЬ (искусственного
  // потолка нет), а вот картинки — тяжёлые: каждая едет мостом строкой base64. В вебе
  // их тоже грузит только то, что видно (loading="lazy"), поэтому здесь готовим первые
  // NATIVE_FACES: остальным стоит буквенная плитка — та же запаска, что в «Чатах», пока
  // фото не загрузилось. Ищет человек по имени — найденный оказывается наверху и получает фото.
  // ФОТО СОСЕДНИХ СТРАНИЦ — ТЕМ ЖЕ КОНВЕЙЕРОМ, но ПОСЛЕ своих (порядок в списке = порядок
  // готовки): свой список из-за чужой папки ждать не должен, а соседняя страница до фото
  // рисуется буквенной плиткой — как любой ряд без фото.
  const faces = useNativePhotos(
    [
      ...shown.slice(0, NATIVE_FACES).map((c) => c.avatarUrl),
      ...pageLists.flatMap((p) => p.list.slice(0, NATIVE_FACES).map((c) => c.avatarUrl)),
    ].filter(Boolean),
  );
  // ФОТО АККАУНТОВ ПАПОК (владелец 12.09.2026, П65): кружок 40 тем же конвейером, что лица
  // рядов, — помеченная папка стоит в полосе не словом, а парой кружков.
  const folderFaces = useNativePhotos(
    folders.folders.flatMap((f) => (f.account?.avatarUrl ? [f.account.avatarUrl] : [])),
    40,
  );
  const fmtShort = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '—';

  // РЯД — ПО ОБРАЗЦУ РЯДА «ЧАТОВ» (владелец 09.09.2026): слева фото или буквенная плитка,
  // имя одной строкой, дата справа, вторая строка — @логин, чипы меток только когда метки
  // есть. Колонки «связь» и слова «без меток» в ряду больше нет — их место в карточке.
  const rowsOf = (list: readonly ContactRow[]): NativeAccountField[] =>
    list.map((c) => {
      const name = contactTitle(c, t('noName'));
      const photo = c.avatarUrl ? faces[c.avatarUrl] : undefined;
      const clean = c.tags;
      const login = c.username ? `@${c.username.replace(/^@/, '')}` : '';
      return {
        key: `ct:${c.id}`,
        kind: 'person' as const,
        title: name,
        // ИМЯ РАВНО ЛОГИНУ — ВТОРОЙ СТРОКИ НЕТ (владелец 09.09.2026, ТЗ «Контакты» п.3):
        // человек не представился, именем стоит его же логин, и «@ivanov» под «@ivanov»
        // — это просто дубль.
        ...(login && login !== name ? { login } : {}),
        ...(clean.length > 0 ? { chips: clean.map((x) => contactTagLabel(x, locale)) } : {}),
        when: fmtShort(c.lastSeenAt || c.createdAt),
        ...(photo ? { photo } : {}),
        // Фото нет — БУКВЕННАЯ ПЛИТКА тем же рецептом, что в «Чатах» (ScrLetterTile):
        // первая буква имени цветом роли, подложка тем же цветом вполсилы. Ни «@», ни
        // силуэта. Цвет считает та же рука, что у кружков записей (circleColor).
        ...(photo ? {} : { letter: contactInitial(name), color: circleColor(name) }),
      };
    });
  const rows = rowsOf(shown);

  // ── ЛИСТ УСЛОВИЙ И ПАПКИ — РОДНЫМИ СТРАНИЦАМИ СТОПКИ «АККАУНТА» (владелец 10.09.2026):
  // строки «пункт → значение → шеврон» и страницы выбора собирает тот же код, что у
  // аудитории рассылки (audience-rows.ts), набор 'contacts' — плюс «Аккаунт». Ключи
  // страниц — с приставкой `page:`, так их запоминает стопка (isNativeSettingsKey).
  const tf = useTranslations('cabinet.contacts.folders');
  const tsec = useTranslations('cabinet.sections');
  const ta = useTranslations('cabinet.audience');
  const tc = useTranslations('cabinet');
  const dict = useAudDict('contacts');
  const rangeLabel = useRangeLabel();
  const rangeWords = (since?: string, until?: string): string =>
    since || until ? rangeLabel({ from: dayDate(since), to: dayDate(until) }) : '';
  const folderWord = useContactFolderWord();
  // Папка, которую правим ('' — новая, ещё не созданная), и ЧЕРНОВИК редактора (владелец
  // 10.09.2026, п.14д): имя и условия правятся на месте и уходят на сервер одним ходом по
  // «Готово»; «назад» черновик бросает. Прежнее «заводится по первому вводу имени и
  // дальше сохраняется сама» человек не понял.
  const [editing, setEditing] = useState('');
  const [draft, setDraft] = useState<{
    name: string;
    rules: ContactFolderRules;
    asAccount: boolean;
  } | null>(null);
  // РЕЖИМ ПРАВКИ СПИСКА ПАПОК (п.14б, канон Apple): вне него — только список; «Править»
  // справа в шапке открывает добавление и восстановление. Текстовую кнопку шапки умеет
  // только свежая сборка (признак `accountTopAction`) — старая держит прежний вид.
  const [editMode, setEditMode] = useState(false);
  const topNative = nativeMethod('accountTopAction');
  const folder = folders.folders.find((f) => f.id === editing) ?? null;
  const rules: ContactFolderRules = draft?.rules ?? {};
  // «ПОКАЗЫВАТЬ КАК АККАУНТ» (владелец 12.09.2026, П65): строка есть, только когда в
  // условиях выбран РОВНО ОДИН аккаунт; иначе метка снимается при сохранении.
  const oneFolderAccount = (rules.accounts ?? []).length === 1 ? (rules.accounts ?? [])[0]! : '';
  // ПРЕВЬЮ ПАПКИ-АККАУНТА (П91): тумблер включён и аккаунт один — вместо поля имени.
  const previewSrc =
    draft?.asAccount && oneFolderAccount
      ? ((data?.accounts ?? []).find((a) => a.key === oneFolderAccount) ?? null)
      : null;
  const previewFaces = useNativePhotos(previewSrc?.avatarUrl ? [previewSrc.avatarUrl] : [], 40);
  const previewAccount = previewSrc
    ? {
        icon: nativeChannelTile(previewSrc.channel === 'ig' ? 'instagram' : 'telegram').icon,
        iconColor: channelHex(previewSrc.channel === 'ig' ? 'instagram' : 'telegram'),
        photo: previewSrc.avatarUrl ? (previewFaces[previewSrc.avatarUrl] ?? '') : '',
        text: shortAccountName(previewSrc.name),
      }
    : null;
  const inFolderEditor = stack.some((k) => k.startsWith(CT_FOLDERS_PAGE));
  // СЧЁТЧИК (п.14г): сколько людей подходит под условия черновика — на каждый выбор.
  const count = useFolderCount(CONTACT_FOLDERS_URL, inFolderEditor && draft ? rules : null);
  const countWords = count === null ? ta('counting') : tf('matchN', { n: count });
  // Значение, которое правят страницы выбора прямо сейчас: папка (её страницы выше
  // в стопке) или воронка.
  const editVal: AudienceValue = inFolderEditor ? rulesToValue(rules) : val;
  const botOptions = useBotOptions([
    ...(editVal.passed ?? []).map((p) => p.draftId),
    ...(editVal.notPassed ?? []),
  ]);
  const letterOptions = useLetterOptions();
  const tagOptions = useTagOptions([...(editVal.tags ?? []), ...(editVal.lacksTags ?? [])]);
  const accountOptions = useMemo<AudOption[]>(
    () =>
      (data?.accounts ?? []).map((a) => ({
        value: a.key,
        // Аккаунт без имени (П90, 13.09.2026): подпись сетью, а не пустая строка.
        label: a.name || tf(a.channel === 'ig' ? 'accountNoNameIg' : 'accountNoNameTg'),
        // Отключённый аккаунт (П90, 13.09.2026) — сноска вместо имени сети, строка остаётся.
        ...(a.off
          ? { hint: tf('accountOff') }
          : a.channel
            ? { hint: a.channel === 'tg' ? 'Telegram' : 'Instagram' }
            : {}),
      })),
    [data, tf],
  );
  const fltOpts = {
    bots: botOptions,
    letters: letterOptions,
    tags: tagOptions,
    accounts: accountOptions,
    range: rangeWords,
    botName: (id: string) => botOptions.find((o) => o.value === id)?.label ?? ta('botGone'),
  };
  /** Правка условий — ТОЛЬКО в черновик; на сервер всё уходит по «Готово». */
  const saveRules = (next: ContactFolderRules): void => {
    setDraft((d) => ({ name: d?.name ?? '', rules: next, asAccount: !!d?.asAccount }));
  };
  const openFolderEditor = (
    f: { id: string; name: string; rules: ContactFolderRules; accountKey?: string | null } | null,
  ): void => {
    setEditing(f?.id ?? '');
    setDraft({ name: f?.name ?? '', rules: f?.rules ?? {}, asAccount: !!f?.accountKey });
  };
  /** «Готово» редактора: одна запись — новая папка или правка имени и условий разом. */
  const saveFolderDraft = (): void => {
    const clean = previewAccount ? previewAccount.text : (draft?.name ?? '').trim();
    if (!clean) return;
    const done = () => {
      folders.reload();
      setDraft(null);
      closeNativeSettingsPage(`${CT_FOLDERS_PAGE}edit`);
    };
    const accountKey = draft?.asAccount && oneFolderAccount ? oneFolderAccount : null;
    if (folder)
      void contactFoldersApi.patch(folder.id, { name: clean, rules, accountKey }).then(done);
    else
      void contactFoldersApi.create(clean, rules, accountKey).then((made) => {
        if (made) folders.setCurrent(made.id);
        done();
      });
  };
  // «Восстановить папки по умолчанию» видно только в правке; «Добавить папку» стоит в
  // пузыре ВСЕГДА (владелец 12.09.2026, П60). Сборка без текстовой кнопки шапки
  // (`accountTopAction`) держит прежний вид — обе кнопки на месте всегда.
  /**
   * КОРЕНЬ ФИЛЬТРА ЕСТЬ В КАТАЛОГЕ ВСЕГДА (владелец 11.09.2026, П10в: «кнопка фильтра не
   * срабатывает»). Натив ищет страницу кнопки в каталоге `pages` В МОМЕНТ ТАПА, а
   * собиралась она только когда её ключ УЖЕ лежал в стопке, — тап проглатывался молча
   * (журнал 11.09, 10:32:17/18/19: три `accountTap ct:flt` подряд и ни одной открытой
   * страницы). Словарь лёгкий: шесть строк условий, — держать его готовым дешевле, чем
   * ждать круга к вебу.
   *
   * INVARIANT(scriptera): страница, на которую смотрит `page` строки или кнопки шапки,
   * обязана быть в `pages` ДО тапа — либо кладётся в стопку вебом (tapRow ниже).
   * Причина: натив открывает только то, что уже в каталоге. Канон:
   * docs/NATIVE-ACCOUNT-PLAN.md, docs/NATIVE-DESIGN-FIXLIST.md П10в.
   */
  const filterRoot = ctFilterRootPage(
    CT_FILTER_PAGE,
    `${CT_FILTER_PAGE}root`,
    tm('filterTitle'),
    val,
    dict,
    fltOpts,
    { key: CT_ROW.reset, label: ta('reset') },
  );
  const extraPages: NativeAccountPage[] = stack.flatMap((k): NativeAccountPage[] => {
    if (k.startsWith(CT_FILTER_PAGE)) {
      // Корень уже собран выше и лежит в каталоге всегда — второй раз его не строим.
      if (innerKey(CT_FILTER_PAGE, k) === 'root') return [];
      const sub = ctFilterSubPage(CT_FILTER_PAGE, k, val, dict, fltOpts);
      return sub ? [sub] : [];
    }
    if (k.startsWith(CT_FOLDERS_PAGE)) {
      const inner = innerKey(CT_FOLDERS_PAGE, k);
      if (inner === 'list') {
        return [
          {
            key: k,
            title: tf('title'),
            // ПОД ЗАГОЛОВКОМ — ОДНО СЛОВО РАЗДЕЛА СЕРЫМ (владелец 14.09.2026), одинаково
            // у «Ботов», «Чатов» и «Контактов».
            subtitle: tsec('contacts'),
            // Список папок — сразу на весь экран стопкой, с героем; правка ложится поверх
            // маленькой полушторкой (решение владельца 14.09.2026).
            present: 'deck',
            // Под заголовком героя — целое предложение (владелец 14.09.2026).
            hero: {
              icon: 'folder',
              color: accountRoleHex('var(--accent-stroke)'),
              title: tf('title'),
              meta: tf('lineHint'),
            },
            // Список по канону Apple (п.14б): справа «Править», в правке — «Готово»,
            // добавление и восстановление видны только в правке.
            ...(topNative
              ? { topAction: { key: CT_ROW.edit, label: editMode ? tc('done') : tf('edit') } }
              : {}),
            groups: [
              {
                key: 'list',
                // Описание живёт под заголовком героя; над пузырём ничего не пишем.
                // ПОРЯДОК — ПЕРЕТАСКИВАНИЕМ В РЕЖИМЕ «ПРАВИТЬ» (П85, владелец 13.09.2026).
                ...(editMode ? { reorder: CT_ROW.order } : {}),
                fields: [
                  // «Создать папку» — первой строкой пузыря (эталон Telegram, владелец
                  // 14.09.2026): добавление стоит сверху списка, а не под ним.
                  {
                    key: CT_ROW.add,
                    kind: 'button' as const,
                    title: tf('add'),
                    tone: 'accent' as const,
                    page: `${CT_FOLDERS_PAGE}edit`,
                  },
                  ...folders.folders.map((f): NativeAccountField => ({
                    key: `ctfd:${f.id}`,
                    kind: 'nav',
                    title: f.name,
                    value: folderWord(f),
                    page: `${CT_FOLDERS_PAGE}edit`,
                    // Удаление — свайпом строки влево (П85), подтверждение родное.
                    dragId: f.id,
                    swipeDelete: `${CT_ROW.del}${f.id}`,
                    swipeLabel: tf('deleteShort'),
                    ...(editMode ? { mark: 'line.3.horizontal' } : {}),
                  })),
                ],
              },
            ],
          },
        ];
      }
      if (inner === 'edit') {
        const root = ctFilterRootPage(
          CT_FOLDERS_PAGE,
          k,
          folder?.name || tf('editTitle'),
          rulesToValue(rules),
          dict,
          fltOpts,
          { key: CT_ROW.folderReset, label: ta('reset') },
          [
            {
              key: 'name',
              label: tf('name'),
              fields: [
                previewAccount
                  ? {
                      key: CT_ROW.name,
                      kind: 'nav' as const,
                      title: '',
                      value: previewAccount.text,
                      disabled: true,
                      preview: previewAccount,
                    }
                  : {
                      key: CT_ROW.name,
                      kind: 'field' as const,
                      title: tf('name'),
                      value: draft?.name ?? '',
                      placeholder: tf('namePh'),
                      max: 30,
                    },
              ],
            },
            {
              key: 'direct',
              hint: tf('directHint'),
              fields: [
                { key: CT_ROW.direct, kind: 'switch', title: tf('directOn'), on: !!rules.direct },
                // «Показывать как аккаунт» (П65) — рядом с «Личными», тем же тумблером.
                ...(oneFolderAccount
                  ? [
                      {
                        key: CT_ROW.asAcc,
                        kind: 'switch' as const,
                        title: tf('asAccount'),
                        on: !!draft?.asAccount,
                      },
                    ]
                  : []),
              ],
            },
          ],
        );
        return [
          {
            ...root,
            // РЕДАКТОР ПАПКИ — СРАЗУ ПОЛНЫМ ЛИСТОМ (решение владельца 14.09.2026): условий
            // у папки много. Полушторка осталась только у «Ботов».
            present: 'deck',
            // Заголовок «Добавить папку» у новой (имя — у существующей), под ним серым
            // слово раздела; справа галочка вместо слова «Готово».
            title: folder ? folder.name : tf('add'),
            subtitle: tsec('contacts'),
            // «Готово» справа в шапке сохраняет черновик (п.14д); сборка без текстовой
            // кнопки получает «Сохранить» кнопкой в пузыре.
            ...(topNative
              ? { topAction: { key: CT_ROW.save, label: tc('done'), icon: 'checkmark' as const } }
              : {}),
            groups: [
              // Счётчик — сноской под пузырём условий (п.14г).
              ...root.groups.map((g) => (g.key === 'rows' ? { ...g, hint: countWords } : g)),
              ...(topNative
                ? []
                : [
                    {
                      key: 'save',
                      fields: [
                        {
                          key: CT_ROW.save,
                          kind: 'button' as const,
                          title: tc('save'),
                          tone: 'accent' as const,
                        },
                      ],
                    },
                  ]),
            ],
          },
        ];
      }
      // Условия папки — полушторками над редактором (п.14в).
      const sub = ctFilterSubPage(CT_FOLDERS_PAGE, k, rulesToValue(rules), dict, fltOpts, 'half');
      if (!sub) return [];
      // МУЛЬТИСЕЛЕКТ ЗАКРЫВАЕТСЯ ГАЛОЧКОЙ (П92): у страниц с галочками «несколько» —
      // ✓ справа сверху; одиночный выбор уходит сам (applySet). Старая сборка видит слово.
      const multi = sub.groups.some((g) => g.fields.some((f) => f.kind === 'check' && !f.radio));
      return [
        multi && topNative
          ? { ...sub, topAction: { key: CT_ROW.pickDone, label: tc('done'), icon: 'checkmark' } }
          : sub,
      ];
    }
    return [];
  });

  // Строки «Показано N из M» больше нет (владелец 16.09: «я не разрешал их писать») —
  // список сам дотягивается по прокрутке, считать людям нечего.
  const shownOf = '';
  const empty =
    contacts.length === 0 ? (data ? t('empty') : '') : shown.length === 0 ? t('filterEmpty') : '';

  const page: NativeAccountPage = {
    key: 'contacts',
    title: hero.title,
    // ГЕРОЯ У СПИСКА НЕТ (владелец 09.09.2026): заголовок «Контакты» стоит в шапке, как у
    // родных списков «Боты» и «Чаты». Плиточная шапка со значком отсюда убрана совсем.
    // Список ещё едет — пузырь ждёт строки пустым, а не мигает «пока никого нет».
    loading: !data,
    // ПОИСК — ОДИН В ОДИН С «БОТАМИ» И «ЧАТАМИ»: плашка-вход под шапкой, тап открывает
    // родную строку поиска сверху (см. tapRow ниже), набранное приезжает событием
    // searchQuery и отбирает его ВЕБ этой же рукой.
    search: { key: 'ct:q', placeholder: t('search'), query: q },
    actions: [
      {
        key: CT_ROW.filter,
        icon: 'line.3.horizontal.decrease',
        label: tm('filterTitle'),
        active: fltOn > 0,
        page: `${CT_FILTER_PAGE}root`,
      },
    ],
    ...(folders.ready && folders.folders.length > 0
      ? {
          folders: {
            current: folders.current,
            // ПАПКА-АККАУНТ — КРУЖКАМИ (П65): помеченной папке едут значок сети, её цвет
            // и фотография аккаунта base64; фото ещё не готово — едет один значок, кружок
            // появится следующим патчем.
            items: folders.folders.map((f) => {
              const chan = f.account ? (f.account.channel === 'ig' ? 'instagram' : 'telegram') : '';
              const face = f.account?.avatarUrl ? (folderFaces[f.account.avatarUrl] ?? '') : '';
              return {
                id: f.id,
                name: f.name,
                count: f.count,
                ...(chan ? { icon: `QuickTile-${chan}`, iconColor: channelHex(chan) } : {}),
                ...(face ? { photo: face } : {}),
              };
            }),
            settingsLabel: tf('settings'),
            // ПЕРВЫЕ СТРАНИЦЫ СОСЕДНИХ ПАПОК (П10б): ряды той же рукой (`rowsOf`) из кэша
            // кабинета; кэш ещё не доехал — запись с пустыми рядами и БЕЗ слов (натив
            // покажет пустую страницу молча, слова приедут вместе с рядами).
            ...(pageLists.length > 0
              ? {
                  pages: pageLists.map((p) => {
                    // Слова пустой страницы — ТЕ ЖЕ, что у своего списка, только условия
                    // берутся у ЭТОЙ папки: есть воронка или условия папки — «ничего не
                    // найдено», иначе «пока никого нет».
                    const ruled =
                      Object.keys(folders.folders.find((f) => f.id === p.id)?.rules ?? {}).length >
                      0;
                    return {
                      id: p.id,
                      fields: rowsOf(p.list),
                      emptyText:
                        !p.resp || p.list.length > 0
                          ? ''
                          : t(fltOn > 0 || ruled ? 'filterEmpty' : 'empty'),
                    };
                  }),
                }
              : {}),
          },
        }
      : {}),
    groups: [
      {
        key: 'contacts',
        ...(shownOf ? { label: shownOf } : {}),
        ...(empty ? { hint: empty } : {}),
        fields: rows,
      },
    ],
  };

  const card = useNativeContactCard(contacts, data?.accounts ?? [], faces, reload);

  // НАБРАННОЕ В РОДНОМ ПОИСКЕ приезжает общим событием searchQuery — тем же, что у
  // «Ботов» и «Чатов» (второго поиска в приложении нет). Слушаем, только пока открыта
  // сама страница: на других страницах «Аккаунта» это чужое слово.
  const hereRef = useRef(here);
  hereRef.current = here;
  useEffect(() => {
    if (!nativeMethod('openSearch')) return;
    const br = nativeBridge();
    if (!br?.addListener) return;
    let dead = false;
    let sub: { remove: () => void | Promise<void> } | null = null;
    void (async () => {
      const s = await br.addListener!('searchQuery', (d) => {
        // Буквы склеиваются (15.2 п.7): патч списка не на каждую, а через 150 мс тишины.
        if (!hereRef.current) return;
        const next = d?.q ?? '';
        if (qTimer.current) window.clearTimeout(qTimer.current);
        qTimer.current = window.setTimeout(() => setQ(next), SEARCH_DEBOUNCE_MS);
      });
      if (dead) void s.remove();
      else sub = s;
    })();
    return () => {
      dead = true;
      void sub?.remove();
    };
  }, []);

  /**
   * КАКАЯ РОДНАЯ СТРАНИЦА СТОИТ ЗА СТРОКОЙ ИЛИ КНОПКОЙ ШАПКИ — по тем же страницам, что
   * уехали нативу (второго списка «ключ → страница» не заводим).
   */
  const pageOf = new Map<string, string>();
  for (const p of [page, filterRoot, ...extraPages]) {
    for (const a of p.actions ?? []) if (a.page) pageOf.set(a.key, a.page);
    for (const g of p.groups) for (const f of g.fields) if (f.page) pageOf.set(f.key, f.page);
  }
  /**
   * ТАП ПО СТРОКЕ СО СТРАНИЦЕЙ (владелец 11.09.2026, П10в). Натив открывает страницу САМ,
   * когда нашёл её в каталоге `pages`, и сообщает об этом вебу; не нашёл — присылает
   * пустую страницу, и тап пропадает молча (журнал 11.09, 10:32:17: `accountTap ct:flt`
   * без «→ страница»). Поэтому ключ в стопку кладёт ВЕБ — тогда страница соберётся по
   * стопке и приедет тем же патчем. Открыл натив — стопка её уже держит, и повтора не
   * будет (проверка `stack.includes`).
   */
  const wantPage = (rowKey: string): void => {
    const key = pageOf.get(rowKey);
    if (!key || stack.includes(key)) return;
    // В журнал — только СТАТИЧЕСКАЯ часть ключа: идентификаторов в него не кладём.
    journalLog('ct.page', { row: rowKey.replace(/:[0-9a-f-]{8,}.*$/i, ':id'), web: 1 });
    pushNativeSettingsPage(key);
  };

  const tapRow = (rowKey: string): boolean => {
    // ПЛАШКА ПОИСКА — вход в ТОТ ЖЕ родной поиск, что у «Ботов» (openSearch): набранное
    // вернётся событием searchQuery. Своего поля у страницы больше нет.
    if (rowKey === 'ct:q') {
      void nativeBridge()?.openSearch?.({ placeholder: t('search') });
      return true;
    }
    // Строка или кнопка со страницей — сперва довести страницу до стопки (см. wantPage),
    // а дальше разбор идёт как раньше: у многих таких строк есть ещё и своё дело.
    wantPage(rowKey);
    if (rowKey === CT_ROW.filter) return true; // страницу открыл натив или стопка выше
    if (rowKey === CT_ROW.reset) {
      setVal({});
      return true;
    }
    if (rowKey === CT_ROW.folderReset) {
      saveRules(valueToRules({}, !!rules.direct));
      return true;
    }
    if (rowKey === CT_ROW.folderSettings) {
      // Шестерёнка полосы папок: страница «Папки» — в стопку «Аккаунта» целиком.
      // РЕЖИМ ПРАВКИ НЕ ПЕРЕЖИВАЕТ ЗАКРЫТИЕ ПОЛУШТОРКИ (владелец 12.09.2026, П61):
      // каким бы способом её ни закрыли (крестик, свайп, «Готово», уход со страницы),
      // следующее открытие начинается обычным списком с «Править». Гасим его здесь —
      // это ЕДИНСТВЕННАЯ дорога к странице папок, а стопку ведёт не этот раздел.
      setEditMode(false);
      openNativeSettingsPage(['settings', 'contacts', `${CT_FOLDERS_PAGE}list`]);
      return true;
    }
    if (rowKey.startsWith(CT_ROW.folderPick)) {
      folders.setCurrent(rowKey.slice(CT_ROW.folderPick.length));
      return true;
    }
    if (rowKey === CT_ROW.edit) {
      setEditMode((v) => !v);
      return true;
    }
    if (rowKey === CT_ROW.save) {
      saveFolderDraft();
      return true;
    }
    if (rowKey === CT_ROW.pickDone) {
      const top = stack[stack.length - 1];
      if (top && top.startsWith(CT_FOLDERS_PAGE)) closeNativeSettingsPage(top);
      return true;
    }
    if (rowKey === CT_ROW.add) {
      openFolderEditor(null);
      return true;
    }
    if (rowKey === CT_ROW.restore) {
      // ПРЕДУПРЕЖДЕНИЕ ПЕРЕД ВОССТАНОВЛЕНИЕМ (п.14а): что вернётся и что свои папки
      // останутся, — родным подтверждением.
      const done = () => void contactFoldersApi.restore().then(folders.reload);
      if (!hasNativeConfirm()) done();
      else
        void askNativeConfirm({
          question: tf('restoreQ'),
          confirmLabel: tf('restoreShort'),
          cancelLabel: tc('cancel'),
          destructive: false,
        }).then((yes) => {
          if (yes) done();
        });
      return true;
    }
    if (rowKey.startsWith(CT_ROW.del)) {
      // Свайп строки списка → красная плашка → сюда (П85): подтверждение — родное.
      const id = rowKey.slice(CT_ROW.del.length);
      const done = () => {
        void contactFoldersApi.remove(id).then(() => {
          folders.reload();
          if (editing === id) {
            setEditing('');
            setDraft(null);
            closeNativeSettingsPage(`${CT_FOLDERS_PAGE}edit`);
          }
        });
      };
      if (!hasNativeConfirm()) done();
      else
        void askNativeConfirm({
          question: tf('deleteQ'),
          confirmLabel: tf('deleteShort'),
          cancelLabel: tc('cancel'),
          destructive: true,
        }).then((yes) => {
          if (yes) done();
        });
      return true;
    }
    if (rowKey.startsWith('ctfd:')) {
      const f = folders.folders.find((x) => x.id === rowKey.slice(5));
      if (!f) return false;
      openFolderEditor(f);
      return true;
    }
    const m = /^ct:(.+)$/.exec(rowKey);
    if (!m) return false;
    const c = contacts.find((x) => x.id === m[1]);
    // Карточка человека — тот же родной ЛИСТ, что в «Чатах» (страницы стопки у неё
    // больше нет): его поднимает веб, наливая содержимое выбранного человека.
    if (c) card.openCard(c.id);
    return true;
  };

  const applySet = (rowKey: string, value: string | boolean): boolean => {
    // Старая сборка рисует своё поле ввода и шлёт набранное сюда — путь оставлен живым
    // (контракт моста заморожен: свежая сборка на это же место ставит плашку-вход).
    if (rowKey === 'ct:q') {
      setQ(typeof value === 'string' ? value : '');
      return true;
    }
    if (rowKey === CT_ROW.order) {
      // Новый порядок из перетаскивания (П85): id через запятую, как отдал натив.
      const ids = typeof value === 'string' ? value.split(',').filter(Boolean) : [];
      if (ids.length > 0) void contactFoldersApi.reorder(ids).then(folders.reload);
      return true;
    }
    if (rowKey === CT_ROW.name) {
      // Имя — в черновик; на сервер уходит по «Готово» вместе с условиями (п.14д).
      const name = typeof value === 'string' ? value : '';
      setDraft((d) => ({ name, rules: d?.rules ?? {}, asAccount: !!d?.asAccount }));
      return true;
    }
    if (rowKey === CT_ROW.asAcc) {
      setDraft((d) => ({ name: d?.name ?? '', rules: d?.rules ?? {}, asAccount: value === true }));
      return true;
    }
    if (rowKey === CT_ROW.direct) {
      saveRules(valueToRules(rulesToValue(rules), value === true));
      return true;
    }
    // Галочки страниц выбора листа условий — тем же разбором, что у аудитории рассылки.
    const audPrefixes: string[] = [
      AUD_KEYS.account1,
      AUD_KEYS.bot,
      AUD_KEYS.botOk,
      AUD_KEYS.botDrop,
      AUD_KEYS.botWhen,
      AUD_KEYS.notPassed1,
      AUD_KEYS.received1,
      AUD_KEYS.has1,
      AUD_KEYS.lacks1,
    ];
    if (audPrefixes.some((k) => rowKey.startsWith(k))) {
      const applyNext = (next: AudienceValue): void => {
        if (inFolderEditor) saveRules(valueToRules(next, !!rules.direct));
        else setVal(next);
      };
      if (rowKey.startsWith(AUD_KEYS.botWhen)) {
        void askPassedRangeNative(
          nativeBridge(),
          editVal,
          rowKey.slice(AUD_KEYS.botWhen.length),
          locale,
          tc('pickRangeTitle'),
          tc('done'),
        ).then((next) => {
          if (next) applyNext(next);
        });
        return true;
      }
      applyNext(audApplySet(editVal, rowKey, value));
      // ОДИНОЧНЫЙ ВЫБОР ЗАКРЫВАЕТ ПОЛУШТОРКУ САМ (П92): «прошёл бота — да/нет/неважно» —
      // один из трёх; выбрали — лист редактора папки уезжает.
      if (inFolderEditor && value === true && rowKey.startsWith(AUD_KEYS.botOk)) {
        const top = stack[stack.length - 1];
        if (top && top.startsWith(CT_FOLDERS_PAGE) && top !== `${CT_FOLDERS_PAGE}edit`)
          closeNativeSettingsPage(top);
      }
      return true;
    }
    return false;
  };

  // Корень фильтра — В КАТАЛОГЕ ВСЕГДА (П10в): натив ищет страницу кнопки в момент тапа.
  return { pages: [page, filterRoot, ...extraPages], tapRow, applySet };
}

export function ContactsSection({
  pick = null,
  tag: tagProp = 'all',
  q: qProp = '',
  searchScope,
}: {
  /** Адресный выбор второй колонки: null — колонки нет (вид прежний, список и нижний
   *  лист), '' — люди отбора, `tag:<метка>` — отбор по метке, иначе транзитный id
   *  человека (открывает карточку модалкой и возвращает адрес списку). */
  pick?: string | null;
  /** Метка из колонки ('all' — все люди). На узком экране не используется. */
  tag?: string;
  /** Строка поиска из колонки. На узком экране не используется. */
  q?: string;
  /** Не используется с ТЗ WEB-LOOK Э2.4: карточка держит свой адрес сама (panel-address.ts),
   *  возвращать адрес к отбору раздел больше не должен. Проп оставлен ради вызова оболочки. */
  onPick?: (p: string) => void;
  /** Ключ КАНАЛА родного поиска слоя (общий механизм: хозяин слоя рисует лупу в шапке,
   *  ввод приходит событием scr-pick-q со своим scope). Задан — веб-строку поиска не
   *  рендерим: двух полей на экране не бывает (владелец 2026-08-07). */
  searchScope?: string;
} = {}) {
  const t = useTranslations('cabinet.contacts');
  // Подписи отбора по каналу и аккаунту — те же слова, что у «Сообщений» (общий словарь).
  const tm = useTranslations('cabinet.messages');
  const tc = useTranslations('cabinet');
  // Активна ли панель стека: только она отдаёт кнопку в шапку (портал PageAction).
  const paneActive = useContext(PaneActiveContext);
  // SPEED 5.6/8.2: прогрев карточек — только у видимой вкладки при видимом документе (pane-visible.ts, О4).
  const paneVisible = usePaneVisible();
  // Отбор на узком экране — локальный выбор страницы (колонки и адресов отбора там нет).
  const [mobQ, setMobQ] = useState('');
  // РЕЖИМ ПОИСКА НА ТЕЛЕФОНЕ (кадр iOS K13-contacts-search): фокус в строке — шапка листа и
  // полоса папок уходят, строка встаёт на линию кружков, справа кружок «×» — выход из поиска.
  const [ctSearching, setCtSearching] = useState(false);
  // ПАПКИ И ФИЛЬТР (владелец 10.09.2026): отбор считает сервер (?folder, ?f), как в
  // приложении; лист условий — тот же, что у аудитории рассылки, плюс «Аккаунт».
  const [mobVal, setMobVal] = useState<AudienceValue>({});
  const mobFolders = useContactFolders();
  const mobFltOn = audienceCount(mobVal);
  const mobPanel = useSlidePanel();
  // Ввод из РОДНОЙ строки поиска слоя — тот же канал, что у пикеров (scr-pick-q + scope).
  useEffect(() => {
    if (!searchScope) return;
    const on = (e: Event): void => {
      const d = (e as CustomEvent<{ q?: string; scope?: string }>).detail;
      if (d?.scope !== searchScope) return;
      setMobQ(d?.q ?? '');
    };
    window.addEventListener('scr-pick-q', on);
    return () => window.removeEventListener('scr-pick-q', on);
  }, [searchScope]);
  const q = pick === null ? mobQ : qProp;
  // На узком виде отбор по метке живёт в листе условий («есть метка»), колонки нет.
  const tag = pick === null ? 'all' : tagProp;

  const listUrl = useMemo(() => {
    const qs = new URLSearchParams();
    // ПАПКА И ФИЛЬТР — НА ЛЮБОЙ ШИРИНЕ (партия Н7): в трёх слоях папки стоят в колонке
    // (ContactsSplitMenu), воронка — кружком в шапке поля; выбор папки общий с полосой
    // телефона (useContactFolders). Считает сервер — как в приложении.
    if (mobFolders.current) qs.set('folder', mobFolders.current);
    if (pick !== null && tag !== 'all') {
      // Старый адрес /app/contacts/tag/<метка> (колонка меток снята в Н7): отбор по метке
      // ТОЖЕ считает сервер (SCALE-PLAN Е2) — теми же правилами, что папка и воронка.
      qs.set('f', JSON.stringify({ ...mobVal, tags: [tag] }));
    } else if (mobFltOn > 0) {
      qs.set('f', JSON.stringify(mobVal));
    }
    const tail = qs.toString();
    return tail ? `${CONTACTS_URL}?${tail}` : CONTACTS_URL;
  }, [pick, tag, mobFolders.current, mobFltOn, mobVal]);
  const { data, contacts, hasMore, busy, loadMore, loading, error, reload } = useContactsPages(
    listUrl,
    true,
  );
  // Форма экрана «Контактов» — снять при первом показе с данными (ред. 9, 7.4.2).
  useShapeRecord('contacts', !!data && !loading && contacts.length > 0);
  // ВХОД В ПУСТУЮ ЗАПОМНЕННУЮ ПАПКУ — НА ШИРОКОМ ЭКРАНЕ ВСТАЁМ НА «ВСЕ» (пакет Д2.2 п.2; то же
  // правило, что у «Чатов», Закон 5 п.5 — один код lib/chats-entry.ts, не копия). Только первый
  // ответ раздела и без поиска/отбора: папку, выбранную человеком внутри раздела, не подменяем.
  // Телефон — тем же правилом (владелец 04.10.2026, пакет М9: пустая папка на входе — дефект на
  // любой ширине).
  const ctEntryDone = useRef(false);
  useEffect(() => {
    if (ctEntryDone.current || !data || loading) return;
    ctEntryDone.current = true;
    if (q || mobFltOn > 0 || contacts.length > 0) return;
    const nf = entryFolder(mobFolders.folders, mobFolders.current, true);
    if (nf !== mobFolders.current) mobFolders.setCurrent(nf);
  });
  const tf = useTranslations('cabinet.contacts.folders');
  const accountOptions = useMemo<PickOption[]>(
    () =>
      (data?.accounts ?? []).map((a) => ({
        value: a.key,
        label: a.name || tf(a.channel === 'ig' ? 'accountNoNameIg' : 'accountNoNameTg'),
        ...(a.off
          ? { hint: tf('accountOff') }
          : a.channel
            ? { hint: a.channel === 'tg' ? 'Telegram' : 'Instagram' }
            : {}),
      })),
    [data, tf],
  );
  const mobFoldersWeb = useContactFoldersWeb(accountOptions);
  // «НАСТРОЙКА ПАПОК» ИЗ КОЛОНКИ ТРЁХ СЛОЁВ (Н7): лист «Папки» открывает сам раздел — тем же
  // ходом, что шестерёнка полосы телефона (у раздела список аккаунтов для редактора папки).
  // Слушает только видимый экземпляр с колонкой: раздел смонтирован ещё и в «Настройках».
  const openFoldersRef = useRef(mobFoldersWeb.open);
  openFoldersRef.current = mobFoldersWeb.open;
  useEffect(() => {
    if (pick === null || !paneActive) return;
    const on = (): void => openFoldersRef.current();
    window.addEventListener(CT_FOLDERS_OPEN_EVENT, on);
    return () => window.removeEventListener(CT_FOLDERS_OPEN_EVENT, on);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- рука листа живёт в ref; входы — есть ли колонка и видна ли панель
  }, [pick === null, paneActive]);
  // ПРОСЬБА ИЗ «НАСТРОЕК» (владелец 13.09.2026, П85: пузырь «Папки» → «Контакты»): в
  // браузере настройки папок открывает САМ раздел — тем же ходом, что его шестерёнка.
  // В приложении просьбы не бывает: там страница папок живёт в стопке «Настроек» и
  // открывается прямо оттуда.

  // Аватарки людей и метки со временем меняются (владелец 2026-08-04): раздел живёт в
  // стеке НЕ размонтируясь, поэтому «показ панели» освежает данные тихо, без скелетона.
  // Своего кода здесь больше нет: с Р5 (владелец 21.08) сигнал показа слушает САМ
  // usePersistentData — копии по страницам сняты, чтобы правило было одно на кабинет.

  // Метки отбора: только присутствующие, по убыванию частоты (потолок — чтобы список не распухал).
  // Каналы и аккаунты отбора — ИЗ САМИХ ДАННЫХ списка (владелец 2026-08-04, как у
  // «Сообщений»): предлагаем только то, что у клиента вправду есть. Имя аккаунта берём
  // из общего свода (API контактов отдаёт его вместе со списком), неизвестный ключ
  // показываем как есть — лучше ключ, чем пустая строка.
  // Секции показываем, только когда выбирать вправду есть из чего: один канал (один
  // аккаунт) на всех людей — отбор бессмысленный.

  // Карточка — общей рукой с родной страницей раздела (useOpenContactCard выше).
  const openCard = useOpenContactCard(reload);
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 К26, кадр iOS K13-contacts-all): кружок-фильтр — в
  // шапке листа, поиск по центру без лупы, полоса папок без счётчиков, строки «аватар 60 ·
  // имя · ник · дата справа». Хуки — ЗДЕСЬ, до ранних выходов (правило хуков): кнопка
  // собирается ниже и отдаётся шапке через ссылку.
  const ctPhone = phoneWebNow() && !paneActive;
  // ВИД списка по кадрам iOS (К26/К30) — на любом телефоне в браузере, в том числе когда
  // «Контакты» стоят страницей «Настроек» (paneActive: кнопка фильтра тогда едет в шапку
  // страницы через PageAction, а не в шторку — это решает ctPhone выше).
  const ctLook = phoneWebNow();
  // Вход в поиск и выход — ходом, как у «Ботов»/«Чатов» (ТЗ ANIM-FIX О5 П5, search-morph.ts):
  // шапка листа и полоса папок гаснут, строка поиска поднимается, список подъезжает.
  const ctBarRef = useRef<HTMLDivElement | null>(null);
  const ctPhase = useSearchMorph(() => {
    const bar = ctBarRef.current;
    if (!bar) return null;
    // Страницей (строка в слоте шапки .main, ТЗ WEB-CAUSES О3) — подъезжает всё поле раздела, как у
    // «Ботов»/«Чатов»; листом — список «Контактов» под строкой.
    const main = bar.closest<HTMLElement>('.main');
    if (main && !bar.closest('.content')) {
      const content = main.querySelector<HTMLElement>(':scope > .content');
      return content ? { vars: main, bar, list: content } : null;
    }
    const list = bar.parentElement?.querySelector<HTMLElement>(':scope > .ct-ios-list');
    if (!list) return null;
    return { vars: bar.closest<HTMLElement>('.drawer') ?? bar.parentElement ?? bar, bar, list };
  }, ctLook && ctSearching);
  const ctSearchCls = searchPhaseClass(ctPhase);
  // ПАПКИ ЛИСТАЮТСЯ ХОДОМ (ТЗ ANIM-FIX О5 П6, folder-pager.tsx): тап по папке и свайп по
  // списку — горизонтальные страницы, как Telegram iOS; отбор — прежний (сервер, ?folder).
  const ctPager = useFolderPager(
    mobFolders.ready ? mobFolders.folders.map((f) => f.id) : [],
    mobFolders.current,
    mobFolders.setCurrent,
  );
  const fltBtnRef = useRef<ReactNode>(null);
  // Кнопку отдаём шапке ТОЛЬКО когда раздел и вправду стоит шторкой «Данных» (там его
  // оборачивает LayerFilterHand). Раздел живёт ещё и в стеке вкладок — НЕАКТИВНОЙ панелью
  // (paneActive = false), и тогда `update` попадал в шапку ЧУЖОГО верхнего листа: окно бота,
  // открытое адресом /app/bots/<id>/stats, получало справа «Фильтр» вместо «⋯» (кадр iOS
  // B07-botwin, сверка 03.10).
  const inDataLayer = useContext(LayerFilterHand) !== null;
  useEffect(() => {
    if (!ctPhone || !inDataLayer) return;
    mobPanel.update({ headerRight: fltBtnRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- кнопка пересобирается каждый рендер; входы — число условий
  }, [ctPhone, mobFltOn]);

  // Карточка человека — МОДАЛКОЙ поверх списка (владелец 2026-08-03, по образцу
  // «Продуктов»): адрес /app/contacts/<id> — транзитный, открыли модалку и вернули
  // адрес текущему отбору (у шторки нет колбэка закрытия — иначе «выбранный» путь
  // повисал бы без открытого окна).
  // АДРЕС У ПАНЕЛИ (ТЗ WEB-LOOK Э2.4): карточка держит /app/contacts/<id>, пока открыта, —
  // адрес к отбору больше не возвращаем (закрытие вернёт его через closeHref/историю); без
  // колонки (телефон, два слоя) выбор берётся из самого адреса — прямой заход открывает карточку.
  const addrPick = useAddressPick('contacts', pick);
  useEffect(() => {
    const pick = addrPick;
    if (!pick || pick.startsWith('tag:')) return; // отбор — не карточка
    if (isOwnPanelHref(sectionUrl('contacts', pick))) return; // окно открыла наша же панель
    const c = contacts.find((x) => x.id === pick) ?? null;
    if (c) {
      // drawer.open зовёт flushSync — из эффекта только отдельной задачей (капкан Э2.1).
      const tm = window.setTimeout(() => openCard(c), 0);
      return () => window.clearTimeout(tm);
    }
    // ЧЕЛОВЕКА НЕТ НА ЗАГРУЖЕННЫХ СТРАНИЦАХ (SCALE-PLAN Е2): список приезжает по 50, а
    // адрес карточки открывают и ссылкой. Спрашиваем его у сервера поимённо — той же
    // ручкой с `?id=`, какой открывает карточку журнал бота.
    // ЧЕРЕЗ КЭШ (PREFETCH-TZ §2.3): ключ `cabinet:contact:<id>` — тот же, каким карточку из журнала бота
    // берёт окно бота (§2.1); повторное открытие ссылкой — из кэша тем же кадром.
    let alive = true;
    void awaitPersistent<ContactsResp>(
      `cabinet:contact:${pick}`,
      `${CONTACTS_URL}?id=${encodeURIComponent(pick)}`,
      { accept: acceptList },
    ).then((j) => {
      const row = j?.ok ? j.contacts?.[0] : null;
      if (alive && row) openCard(row);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openCard пересоздаётся каждый рендер; входы — адрес и приехавший список
  }, [addrPick, data]);

  // ПОИСК ПО ВСЕЙ БАЗЕ (SCALE-PLAN Е2, приём «Чатов»): сразу отбираем загруженное, следом
  // приезжают находки сервера. В кабинете лежит одна страница — своим отбором поиск нашёл
  // бы только тех, кто уже на экране.
  const needle = q.trim();
  const found = useContactsSearch(listUrl, needle);
  const searched = !!needle && found?.q === needle;

  // ДОГРУЗКА САМА (как у «Чатов», О22.4): конец списка подъехал к экрану — просим
  // следующую страницу. Ловушка стоит ЗА последним рядом с запасом в пол-экрана, кнопки
  // «Показать ещё» нет. Во время поиска списка страниц нет: на экране находки.
  const moreRef = useRef<HTMLDivElement | null>(null);
  const autoMore = hasMore && !needle;
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !autoMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [autoMore, loadMore]);

  // ПОКА ПАПКА ГРУЗИТСЯ ИЛИ УПАЛА — НА МЕСТЕ ТОЛЬКО СПИСОК (TARIFF-PAGE-TZ §11 П7, владелец 11.10.2026: «папки то
  // нажимаются, то нет»). Прежде два ранних `return` отдавали один каркас/ошибку БЕЗ строки поиска, полосы папок и
  // кружка фильтра: клик по папке без кэша на 2–3 с стирал всё поле, и второй клик попадал в пустоту. Шапка
  // (поиск, папки, фильтр) живёт всегда; каркас и ошибка — вместо списка.
  // ЗАГЛУШКА ФОРМЫ (ред. 9 §2.3): таблица тех же строк, что у этого списка при прошлом показе, — вместо трёх ручных.
  const hold = loading ? (
    <Shape name="contacts" groupsClass="" groupClass="card ct-table" />
  ) : error && !data ? (
    <div className="ai-empty">
      {t('loadError')}{' '}
      <KButton tone="link" onClick={reload}>
        {t('retry')}
      </KButton>
    </div>
  ) : null;

  // Находки сервера проходят ТОЙ ЖЕ меркой, что загруженное: мерка на экране одна.
  const shown = filterContacts(searched ? found.contacts : contacts, q, tag);

  // ПУСТО В ТЕКУЩЕМ ОТБОРЕ. На широком экране (колонка отбора стоит слева) — одна строка.
  // НА УЗКОМ ПОИСК, ПАПКИ И ВОРОНКА ОСТАЮТСЯ НА МЕСТЕ (К3.4, владелец 02.10.2026), как в
  // iOS-приложении: пустой может быть просто первая папка, и без полосы папок человек из неё
  // не выйдет — раздел выглядел пустым целиком, хотя люди лежали в соседних папках.
  const emptyNow = contacts.length === 0 && !searched;

  // ОДИН плоский список-таблица (владелец 2026-08-03: без групп по каналам — канал
  // помечен значком-плиткой у строки). Живые сверху (последняя активность). На широком —
  // колонки Имя · Связь · Метки · Был(а) (шапка .ct-head), на узком CSS складывает ряд
  // в две строки и прячет колонки связи/даты (мобильный вид — не таблица).
  const flat = [...shown].sort((a, b) => (b.lastSeenAt || '').localeCompare(a.lastSeenAt || ''));
  // КАРТОЧКИ ВИДИМЫХ ЛЮДЕЙ И ПОЛЭКРАНА ВПЕРЁД (PREFETCH-CAREFUL-TZ §2 п.1, владелец 11.10.2026: «аккуратный
  // прогрев»): ряды `.ct-row` помечены `data-warm-id`; прокрутили — догреваются следующие. Прежний прогрев «все
  // карточки списка» (PREFETCH-TZ §3 п.1) отменён. С экрана греется только история; остальное — по касанию.
  const byId = useMemo(() => new Map(flat.map((c) => [c.id, c])), [flat]);
  const warmCard = useCallback(
    (id: string) => {
      const c = byId.get(id);
      if (c) warmContactCard(c);
    },
    [byId],
  );
  const flatIds = useMemo(() => flat.map((c) => c.id), [flat]);
  // Карточка — только строке, пробывшей в окне порог плана (ТЗ SPEED §5.3, как ленты «Чатов» §8.2).
  useWarmInView(ctPager.wrapRef, flatIds, warmCard, paneVisible, true);
  const list = hold ? (
    hold
  ) : emptyNow ? (
    ctLook ? (
      <ListEmptyNote text={t('empty')} />
    ) : (
      <DEmpty>{t('empty')}</DEmpty>
    )
  ) : (
    <>
      {shown.length === 0 &&
        (ctLook ? <ListEmptyNote text={t('filterEmpty')} /> : <DEmpty>{t('filterEmpty')}</DEmpty>)}
      {/* Пустую таблицу на телефоне не рисуем — торчала белой полосой под лотком (К30). */}
      {ctLook && flat.length === 0 ? null : (
        <ContactTable shape="contacts" contacts={flat} onOpen={openCard} onWarm={(c) => warmContactCard(c, true)} />
      )}
      {/* АВТОЗАГРУЗКА ВМЕСТО «ПОКАЗАТЬ ЕЩЁ» (как в «Чатах»): ловушка наблюдателя и обычный
          скелетон ряда за последней строкой — страница успевает приехать до того, как
          человек упрётся в конец. Класс общий со списком переписок: хвост у них один. */}
      {autoMore && (
        <div className="msg-more sk-hold" ref={moreRef} aria-busy={busy}>
          <div className="scn-trow msg-trow">
            <div className="scn-trow-main">
              <Sk w={40} h={40} r="round" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <Sk w={140} h={13} />
                <Sk w={200} h={11} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ── Узкий экран (колонки нет): страница с поиском; отбор — кружком в шапке ──────────
  // Айфон-шапка узкого веба (владелец 2026-08-04): отбор по меткам — круглой кнопкой
  // фильтра СПРАВА в шапке страницы (портал PageAction в #cab-topact), чипов в теле нет.
  // По тапу кружка выезжает НИЖНИЙ ЛИСТ фильтров (владелец 2026-08-05, IMG_1278) — общий
  // механизм FilterButton, а не выпадающее меню. В шторке «Данных» портал заглушён
  // (PaneActiveContext=false): кружок остаётся в теле шторки, прижатый вправо, и лист
  // выезжает ПОВЕРХ слоя стопкой (канон §11).
  // Секции листа (владелец 2026-08-04): «Метки» + «Канал» и «Аккаунт» — как у «Сообщений».
  // Ключи пунктов с приставкой: метка человека может быть любым словом.
  const openFilter = (): void =>
    mobPanel.open({
      title: tm('filterTitle'),
      sheet: true,
      // Телефон (кадры iOS K13-contacts-filter / -filter-account): полулист-стопка с шапкой
      // «× · Фильтр» по центру; страницы условий — «‹ · имя» в шапке, строки с галочкой справа.
      ...(phoneWebNow() ? { sheetDetent: 0.5 } : {}),
      winKey: 'ct-filter',
      body: (
        <AudienceFilterSheet
          value={mobVal}
          onChange={(v) => setMobVal(v)}
          only="contacts"
          accounts={accountOptions}
          {...(phoneWebNow() ? { valueRight: true, headTitles: true } : {})}
        />
      ),
    });
  // Воронка — тот же кружок шапки, что был (acctbtn + flt-on), но открывает ЛИСТ условий
  // рассылки, а не всплывающий список (владелец 10.09.2026).
  const fltBtn = (
    <KBareButton
      className={`acctbtn${mobFltOn > 0 ? ' flt-on' : ''}`}
      aria-label={tm('filterTitle')}
      title={tm('filterTitle')}
      onClick={openFilter}
    >
      <FilterGlyph on={mobFltOn > 0} />
      {mobFltOn > 0 ? (
        <span className="flt-badge" aria-hidden>
          {mobFltOn}
        </span>
      ) : null}
    </KBareButton>
  );
  fltBtnRef.current = fltBtn;
  // ── Три слоя: поиск и папки — в колонке (ContactsSplitMenu, партия Н7), поле — люди
  // папки. Шапка поля = шапка телефона без «‹»: имя папки (на телефоне — «Контакты»; в
  // трёх слоях раздел подписан колонкой, а поле — выбранной папкой, как «Боты»), справа
  // кружок фильтра (кадр iOS K13-contacts: воронка в правом углу шапки).
  if (pick !== null) {
    const cur = mobFolders.folders.find((f) => f.id === mobFolders.current);
    return (
      <>
        <div className="split-pane-t pane-head">
          <span className="pane-head-t">{tag !== 'all' ? tag : (cur?.name ?? t('paneAll'))}</span>
          <span className="pane-head-act">{fltBtn}</span>
        </div>
        {list}
      </>
    );
  }
  const foldersStrip =
    mobFolders.ready && mobFolders.folders.length > 0 ? (
      <FoldersStrip
        folders={mobFolders.folders.map((f) => ({
          id: f.id,
          name: f.name,
          unread: ctLook ? 0 : f.count,
          ...(f.account ? { account: f.account } : {}),
        }))}
        current={mobFolders.current}
        onPick={ctPager.pick}
        link={ctPager.strip}
        onSettings={() => mobFoldersWeb.open()}
      />
    ) : null;
  return (
    <>
      {paneActive ? (
        <PageAction>{fltBtn}</PageAction>
      ) : ctPhone ? null : (
        <FilterRow>{fltBtn}</FilterRow>
      )}
      {/* Строка поиска — ОБЩИЙ каркас разделов (.ai-bar-shell): те же отступы под верхом
          и то же плавное схлопывание при открытии родного поиска, что у «Ботов» и
          «Сообщений». В приложении поле не рендерится вовсе: поиск даёт лупа в шапке
          слоя (searchScope), и второго поля на экране не бывает. */}
      {/* Строка и полоса папок — в слоты шапки списка, когда «Контакты» стоят страницей (ТЗ WEB-CAUSES
          О3: полоса липнет под строку кружков при любой длине списка); в листе — на прежнем месте. */}
      {!searchScope && (
        <HeadSearch>
          <ListSearch
            barRef={ctBarRef}
            className={ctSearchCls || undefined}
            value={mobQ}
            onChange={setMobQ}
            placeholder={t('search')}
            searching={ctLook && ctSearching}
            {...(ctLook ? { onEnter: () => setCtSearching(true) } : {})}
            onExit={() => {
              setMobQ('');
              setCtSearching(false);
            }}
            closeLabel={tc('close')}
          />
        </HeadSearch>
      )}
      {foldersStrip ? <HeadFolders>{foldersStrip}</HeadFolders> : null}
      <div className={ctLook ? 'ct-ios-list' : undefined}>
        <FolderPage pager={ctPager}>{list}</FolderPage>
      </div>
    </>
  );
}
